import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { useDepartment } from '../../contexts/DepartmentContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import PmRequesterPicker from '../../components/pm/PmRequesterPicker';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../../components/ui/dropdown-menu';
import { ChevronLeft, ChevronRight, Plus, Trash2, Clock, MapPin, Users, Briefcase, X, Camera, FileText, StickyNote, Save, Search, Send, Bell, Pencil, Maximize2, Minimize2, Phone, Play, CheckCircle, Printer, Lock, AlertTriangle, Star } from 'lucide-react';
import { toast } from 'sonner';
import QuickAddCustomerDialog from '../../components/QuickAddCustomerDialog';
import CustomerEditDialog from '../../components/CustomerEditDialog';
import NotifyCustomerDialog from '../../components/communication/NotifyCustomerDialog';
import RefreshIndicator from '../../components/RefreshIndicator';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import OnCallBanner from '../../components/OnCallBanner';
import { isoDateET, weekStartSaturdayET, addDaysET, fmtETWeekday, fmtETNumeric, fmtETMonthDay, todayISO_ET } from '../../utils/tz';
import UnscheduledRail, { ScheduleItemDialog } from '../../components/UnscheduledRail';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AdminCompletionEditSheet, { canCorrectCompletion } from '../../components/AdminCompletionEditSheet';
import MoveUpListSheet from '../../components/MoveUpListSheet';
import SchedulePrintDialog from '../../components/SchedulePrintDialog';
import FieldCompletionDetails from '../../components/FieldCompletionDetails';
import { Thumbnail } from '../../components/LightboxProvider';
import { useMapsNav } from '../../components/MapsNavProvider';
import { fmtLocalTime } from '../../lib/timeFmt';
import AddVisitSheet from '../../components/schedule/AddVisitSheet';
import { uniqueDivisionKeys } from '../../lib/divisions';
import OriginalRecordNote from '../../components/OriginalRecordNote';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fallback color palette per service-type-name keyword (used only when API-loaded types don't carry one).
const COLOR_FOR_NAME = (name) => {
  const n = (name || '').toLowerCase();
  if (n.includes('drain')) return '#7C3AED';
  if (n.includes('water heater') || n.includes('tankless')) return '#2563EB';
  if (n.includes('toilet') || n.includes('faucet')) return '#0891B2';
  if (n.includes('leak')) return '#059669';
  if (n.includes('repipe')) return '#CA8A04';
  if (n.includes('inspect')) return '#9333EA';
  if (n.includes('emergency') || n.includes('main line') || n.includes('sewer')) return '#DC2626';
  if (n.includes('pto') || n.includes('time off')) return '#991B1B';
  if (n.includes('training') || n.includes('meeting') || n.includes('inventory') || n.includes('shop')) return '#525252';
  return '#D4AF37';
};

// --- Date utilities ---
// iter65 — All "what day is it" computations now go through ET-anchored helpers
// so a viewer outside America/New_York doesn't see the wrong "today" highlight or
// week boundaries. Shift date/start/end strings remain TZ-naive.
const toISODate = isoDateET;
// iter149 — Schedule weekly grid runs Saturday → Friday to match the Nexflow
// Plumbing payroll/business week. Previously this was Sunday-start (weekStartET);
// we switched to weekStartSaturdayET so the schedule grid lines up with
// AdminTimesheets and the payroll CSV export.
const getWeekStart = weekStartSaturdayET;
const addDays = addDaysET;
const formatTime12 = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'p' : 'a';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')}${period}`;
};
const computeHours = (start, end) => {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  return Math.max(0, (eh + em / 60) - (sh + sm / 60));
};

// iter144 — Hoisted timestamp helpers shared by ShiftCard's status tag and the
// Edit Shift modal's Actual-Time controls. Keeps both views identical.
// iter165 — Always render in COMPANY_TZ (America/New_York). The backend stores
// UTC but sometimes serialises without a `Z` suffix; the shared helper handles
// both shapes and never trusts the browser's local TZ.
const fmtT = (iso) => fmtLocalTime(iso);
const fmtDur = (m) => {
  if (m == null) return '';
  const h = Math.floor(m / 60); const mm = m % 60;
  return h > 0 ? `${h}h ${mm}m` : `${mm}m`;
};

// Format an "HH:MM" (24h) clock string to a friendly 12h label (e.g. "1:00 PM").
const fmtClock12 = (hhmm) => {
  if (!hhmm || typeof hhmm !== 'string' || !hhmm.includes(':')) return hhmm || '';
  const [hStr, mStr] = hhmm.split(':');
  let h = parseInt(hStr, 10);
  if (Number.isNaN(h)) return hhmm;
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12; if (h === 0) h = 12;
  return `${h}:${mStr} ${ampm}`;
};
const timeOffWindowLabel = (shift) => {
  if (shift.time_off_partial && shift.start_time && shift.end_time) {
    return `${fmtClock12(shift.start_time)}–${fmtClock12(shift.end_time)}`;
  }
  return 'Unavailable all day';
};

const ShiftCard = ({ shift, onClick, onDragStart, onDragEnd }) => {
  const color = shift.color || '#D4AF37';
  const title = shift.shift_title || shift.service_type;
  const isDraft = shift.status === 'draft';
  // iter232 — Approved time-off blocks render distinctly and are NOT draggable /
  // claimable / editable like a job. Clicking opens a read-only details view.
  // iter275 — Holiday block (blue, all-day, VIRTUAL — never a real appointment).
  // Non-draggable / non-editable; mirrors the red PTO block treatment.
  if (shift.is_holiday_block) {
    const hrs = shift.paid_holiday_hours;
    return (
      <div
        data-testid={`holiday-block-${shift.id}`}
        style={{
          borderLeft: '4px solid #2563EB',
          backgroundImage: 'repeating-linear-gradient(135deg, rgba(37,99,235,0.14) 0px, rgba(37,99,235,0.14) 8px, rgba(37,99,235,0.05) 8px, rgba(37,99,235,0.05) 16px)',
        }}
        className="w-full max-w-full min-w-0 text-left bg-[#0d1424] rounded-md p-2 mb-1 border border-blue-900/50 [overflow-wrap:anywhere] break-words"
        title="Paid company holiday"
      >
        <div className="flex items-center gap-1 text-blue-300 text-[10px] font-bold uppercase tracking-wider mb-0.5">
          <Star className="w-3 h-3" /> Holiday
        </div>
        <div className="text-white text-xs leading-tight break-words">{shift.holiday_name || 'Company Holiday'}</div>
        <div className="text-blue-200/80 text-[10px] mt-0.5">
          All day{(typeof hrs === 'number' && hrs > 0) ? ` · ${hrs}h paid` : ''}
        </div>
      </div>
    );
  }
  if (shift.is_time_off_block) {
    const hrs = shift.paid_leave_hours;
    const masked = shift.time_off_masked;
    const windowLabel = timeOffWindowLabel(shift);
    return (
      <button
        onClick={onClick}
        draggable={false}
        data-testid={`pto-block-${shift.id}`}
        style={{
          borderLeft: '4px solid #DC2626',
          backgroundImage: 'repeating-linear-gradient(135deg, rgba(220,38,38,0.14) 0px, rgba(220,38,38,0.14) 8px, rgba(220,38,38,0.05) 8px, rgba(220,38,38,0.05) 16px)',
        }}
        className="w-full max-w-full min-w-0 text-left bg-[#1a0f0f] hover:bg-[#241414] rounded-md p-2 mb-1 transition-colors duration-150 border border-red-900/50 cursor-pointer [overflow-wrap:anywhere] break-words"
        title="Approved time off — this employee is unavailable. Click to view details."
      >
        <div className="flex items-center gap-1 text-red-300 text-[10px] font-bold uppercase tracking-wider mb-0.5">
          <Lock className="w-3 h-3" />
          {masked ? 'Scheduled Off — Approved' : 'PTO — Approved Time Off'}
        </div>
        {!masked && (
          <div className="text-white text-xs leading-tight [overflow-wrap:anywhere] break-words">{title || 'Time Off'}</div>
        )}
        <div className="text-red-200/80 text-[10px] mt-0.5">
          {windowLabel}{(!masked && typeof hrs === 'number' && hrs > 0) ? ` · ${hrs}h paid` : ''}
        </div>
        {shift.time_off_conflict && (
          <div
            data-testid={`pto-conflict-badge-${shift.id}`}
            className="mt-1 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-amber-300 border border-amber-500/50 bg-amber-500/10 px-1 rounded"
          >
            <AlertTriangle className="w-2.5 h-2.5" /> Override — job assigned
          </div>
        )}
      </button>
    );
  }
  return (
    <button
      onClick={onClick}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', shift.id);
        onDragStart && onDragStart(shift);
      }}
      onDragEnd={() => onDragEnd && onDragEnd()}
      data-testid={`shift-card-${shift.id}`}
      style={{ borderLeft: `4px solid ${color}`, borderStyle: isDraft ? 'dashed' : 'solid' }}
      className={`w-full max-w-full min-w-0 text-left ${isDraft ? 'bg-[#0F0F0F]' : 'bg-[#1A1A1A]'} hover:bg-[#262626] rounded-md p-2 mb-1 transition-all duration-150 border border-[#262626] cursor-grab active:cursor-grabbing ${isDraft ? 'opacity-80' : ''} [overflow-wrap:anywhere] break-words`}
    >
      <div className="flex items-center justify-between gap-1 flex-wrap">
        <div className="text-[#D4AF37] text-xs font-bold mb-0.5">
          {shift.all_day ? 'All day' : `${formatTime12(shift.start_time)} – ${formatTime12(shift.end_time)}`}
        </div>
        {isDraft && <span className="text-[8px] font-bold uppercase tracking-wider text-[#525252] bg-[#262626] px-1 rounded" data-testid={`shift-draft-badge-${shift.id}`}>Draft</span>}
        {!isDraft && shift.claimable && !shift.employee_id && (
          <span
            data-testid={`shift-claimable-badge-${shift.id}`}
            className="text-[8px] font-bold uppercase tracking-wider text-[#D4AF37] border border-[#D4AF37]/60 bg-[#D4AF37]/10 px-1 rounded"
          >
            Claimable
          </span>
        )}
        {!isDraft && shift.claimed_via === 'self_claim' && (
          <span
            data-testid={`shift-self-claimed-badge-${shift.id}`}
            className="text-[8px] font-bold uppercase tracking-wider text-green-400 border border-green-500/40 bg-green-500/10 px-1 rounded"
            title={shift.claimed_at ? `Claimed ${new Date(shift.claimed_at).toLocaleString()}` : 'Self-claimed by tech'}
          >
            Self-Claimed
          </span>
        )}
        {/* iter124 Wave M — "Wants Earlier" badge so dispatch spots move-up requests on the grid.
            iter125 — Purple color family (set aside for Move-Up); red still wins for HIGH priority. */}
        {shift.move_up_requested && (
          <span
            data-testid={`shift-moveup-badge-${shift.id}`}
            title={shift.move_up_notes ? `Move-Up: ${shift.move_up_notes}` : 'Customer wants earlier opening'}
            className={`text-[8px] font-bold uppercase tracking-wider px-1 rounded border ${
              shift.move_up_priority === 'high'
                ? 'text-red-300 border-red-500/50 bg-red-500/10'
                : 'text-purple-300 border-purple-500/50 bg-purple-500/10'
            }`}
          >
            ↑ Earlier
          </span>
        )}
        {/* iter169 — Partial closeout: completion submitted but invoice decision never picked. */}
        {shift.needs_invoice_decision && (
          <span
            data-testid={`shift-finish-billing-badge-${shift.id}`}
            title="Closeout submitted but invoice decision missing — click to finish."
            className="text-[8px] font-bold uppercase tracking-wider text-red-200 border border-red-500/60 bg-red-500/20 px-1 rounded"
          >
            $ Finish Billing
          </span>
        )}
      </div>
      <div className="text-white text-xs leading-tight line-clamp-2 [overflow-wrap:anywhere] break-words">{title}</div>
      {shift.job_number && (
        <div data-testid={`shift-visit-badge-${shift.id}`} className="text-[9px] font-bold uppercase tracking-wider text-[#D4AF37]/90 mt-0.5">{shift.job_number} · Visit {shift.visit_number || 1}</div>
      )}
      {shift.customer_name && (
        <div className="text-[#A3A3A3] text-[10px] mt-0.5 truncate [overflow-wrap:anywhere]" title={shift.customer_name}>{shift.customer_name}</div>
      )}
      {/* iter143 — Dispatcher day-progress status line. Preserves the arrival window
          above; this row tells dispatch at-a-glance whether the tech is on-job, done,
          or still pending — without opening the shift. */}
      {(() => {
        // iter165 — always render times in COMPANY_TZ.
        const fmtT = (iso) => fmtLocalTime(iso);
        const fmtDur = (m) => {
          if (m == null) return '';
          const h = Math.floor(m / 60); const mm = m % 60;
          return h > 0 ? `${h}h ${mm}m` : `${mm}m`;
        };
        const status = (shift.status || '').toLowerCase();
        const cancelled = status === 'cancelled' || status === 'canceled';
        if (cancelled) {
          return (
            <div data-testid={`shift-status-${shift.id}`} className="mt-1 text-[10px] font-bold uppercase tracking-wider text-red-300 border border-red-500/40 bg-red-500/10 px-1 rounded inline-block">
              Cancelled
            </div>
          );
        }
        if (shift.actual_end_time || status === 'completed') {
          return (
            <div data-testid={`shift-status-${shift.id}`} className="mt-1 text-[10px] font-bold uppercase tracking-wider text-green-300 border border-green-500/40 bg-green-500/10 px-1 rounded inline-block">
              Done {fmtT(shift.actual_end_time || shift.completed_at)}
              {shift.actual_duration_minutes != null ? ` · ${fmtDur(shift.actual_duration_minutes)}` : ''}
            </div>
          );
        }
        if (shift.actual_start_time || status === 'in_progress') {
          return (
            <div data-testid={`shift-status-${shift.id}`} className="mt-1 text-[10px] font-bold uppercase tracking-wider text-blue-300 border border-blue-500/40 bg-blue-500/10 px-1 rounded inline-block">
              On Job {fmtT(shift.actual_start_time)}
            </div>
          );
        }
        if (shift.claimed_at && shift.employee_id) {
          return (
            <div data-testid={`shift-status-${shift.id}`} className="mt-1 text-[10px] font-bold uppercase tracking-wider text-[#D4AF37] border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-1 rounded inline-block">
              Claimed
            </div>
          );
        }
        return null;
      })()}
      {/* iter220 — Crew chips (prefer canonical crew_assignments; fall back to legacy helpers). */}
      {(() => {
        const crew = (shift.crew_assignments && shift.crew_assignments.length)
          ? shift.crew_assignments.filter((c) => !c.is_primary)
          : (shift.helpers || []);
        if (!crew.length) return null;
        const ROLE_ABBR = { assigned_technician: 'Tech', helper: 'Helper', office_support: 'Office', other: 'Crew', primary_lead: 'Lead' };
        return (
          <div className="mt-1 flex flex-wrap gap-0.5" data-testid={`shift-helpers-${shift.id}`}>
            {crew.slice(0, 3).map((h) => (
              <span
                key={h.employee_id}
                data-testid={`shift-crew-chip-${shift.id}-${h.employee_id}`}
                className="text-[9px] px-1 rounded bg-blue-500/10 text-blue-300 border border-blue-700/40"
                title={`${h.name} — ${ROLE_ABBR[h.role] || h.role}`}
              >
                +{(h.name || '').split(' ')[0]}
              </span>
            ))}
            {crew.length > 3 && (
              <span className="text-[9px] px-1 rounded bg-[#262626] text-[#A3A3A3]">+{crew.length - 3}</span>
            )}
          </div>
        );
      })()}
    </button>
  );
};

// iter125 — Manual day-status helpers. Manual override takes precedence over
// auto capacity. Roles allowed to flip status; techs see read-only.
const DAY_STATUS_EDIT_ROLES = new Set(['super_admin', 'admin', 'manager', 'service_manager',
                                        'office_staff', 'construction_manager', 'dispatcher']);
const dayStatusTone = (status) => (
  status === 'FULL'    ? { bg: 'bg-red-500/15',    text: 'text-red-300',    border: 'border-red-700/50' } :
  status === 'LIMITED' ? { bg: 'bg-amber-500/15',  text: 'text-amber-300',  border: 'border-amber-700/50' } :
                         { bg: 'bg-green-500/15', text: 'text-green-300',  border: 'border-green-700/50' }
);

const DayHeader = ({ date, shifts, capacity, dayStatusOverride, canEditStatus, onEditStatus }) => {
  // iter232 — Time-off blocks are all-day internal markers; exclude them from the
  // day's job hours + job count so they never inflate capacity/totals.
  const jobShifts = shifts.filter((s) => !s.is_time_off_block && !s.is_holiday_block);
  const totalHours = jobShifts.reduce((acc, s) => acc + computeHours(s.start_time, s.end_time), 0);
  const uniqueUsers = new Set(jobShifts.filter(s => s.employee_id).map(s => s.employee_id)).size;
  const day = fmtETWeekday(date);
  const num = fmtETNumeric(date);
  const today = toISODate(date) === todayISO_ET();
  // iter106 — Company capacity (auto). iter125 — Manual override wins.
  const effectiveStatus = dayStatusOverride?.status || capacity?.status || null;
  const capTone = effectiveStatus ? dayStatusTone(effectiveStatus) : null;
  const isManual = !!dayStatusOverride?.status;
  const autoUtil = capacity ? `${Math.round((capacity.utilisation || 0) * 100)}% utilised · ${Math.round((capacity.booked_minutes || 0) / 60)}h booked of ${Math.round((capacity.capacity_minutes || 0) / 60)}h capacity` : '';
  const tooltip = isManual
    ? `Manual: ${effectiveStatus}${dayStatusOverride.note ? ' — ' + dayStatusOverride.note : ''}${dayStatusOverride.set_by_name ? ' (set by ' + dayStatusOverride.set_by_name + ')' : ''}${autoUtil ? '\nAuto: ' + autoUtil : ''}`
    : autoUtil;
  return (
    <div className={`px-3 py-2 border-b border-[#262626] ${today ? 'bg-[#D4AF37]/5' : ''}`}>
      <div className="flex items-baseline justify-between">
        <span className={`text-xs font-bold uppercase tracking-wider ${today ? 'text-[#D4AF37]' : 'text-white'}`}>{day} {num}</span>
        {effectiveStatus && (
          <button
            type="button"
            data-testid={`capacity-badge-${toISODate(date)}`}
            disabled={!canEditStatus}
            onClick={() => canEditStatus && onEditStatus && onEditStatus(toISODate(date))}
            className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border inline-flex items-center gap-1 ${capTone.bg} ${capTone.text} ${capTone.border} ${canEditStatus ? 'cursor-pointer hover:opacity-90' : 'cursor-default'}`}
            title={tooltip}
          >
            {effectiveStatus}
            {isManual && <span className="text-[7px] opacity-80" data-testid={`capacity-manual-pin-${toISODate(date)}`}>●</span>}
          </button>
        )}
        {!effectiveStatus && canEditStatus && (
          <button
            type="button"
            data-testid={`capacity-set-${toISODate(date)}`}
            onClick={() => onEditStatus && onEditStatus(toISODate(date))}
            className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border border-[#262626] text-[#525252] hover:text-white hover:border-[#3f3f3f]"
            title="Set day status"
          >
            Set
          </button>
        )}
      </div>
      <div className="flex items-center gap-3 mt-1 text-[10px] text-[#A3A3A3]">
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{totalHours.toFixed(1)}h</span>
        <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{jobShifts.length}</span>
        <span className="flex items-center gap-1"><Users className="w-3 h-3" />{uniqueUsers}</span>
      </div>
    </div>
  );
};

const Schedule = () => {
  const { activeDepartment } = useDepartment();
  const [weekStart, setWeekStart] = useState(getWeekStart(new Date()));
  const [appointments, setAppointments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [allEmpById, setAllEmpById] = useState({});   // iter256 — incl. inactive/archived, for orphan-assignee rows
  const [serviceTypes, setServiceTypes] = useState([]);    // [{id, name, customer_required}]
  const [jobTemplates, setJobTemplates] = useState([]);    // [{id, name, service_type, dispatcher_notes, checklist}]
  const [modalOpen, setModalOpen] = useState(false);
  // iter221 — Recurring Shift builder (Phase B)
  const [recurringOpen, setRecurringOpen] = useState(false);
  const recInitial = () => ({
    shift_title: '', service_type: '', start_date: '', end_date: '', occurrences: '',
    end_mode: 'date', start_time: '08:00', end_time: '08:30', repeat: 'weekly',
    weekdays: ['Monday', 'Tuesday', 'Wednesday'], is_internal_activity: true,
    customer_id: '', work_order_number: '', dispatcher_notes: '', crew: [], employee_id: '', status: 'scheduled',
  });
  const [recForm, setRecForm] = useState(recInitial());
  const [recSaving, setRecSaving] = useState(false);
  // Master-Schedule "Add Time Off" → routes through the canonical /timeoff flow.
  const [timeOffOpen, setTimeOffOpen] = useState(false);
  const timeOffInitial = () => ({
    employee_id: '', time_off_type: 'pto', start_date: toISODate(new Date()),
    end_date: '', reason: '', override: false, override_reason: '',
  });
  const [timeOffForm, setTimeOffForm] = useState(timeOffInitial());
  const [timeOffSaving, setTimeOffSaving] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  // iter188 — Unscheduled mode uses the SAME New Shift modal but with all
  // scheduling fields optional and the primary button labelled "Save
  // Unscheduled Job" (routes to persistDraft). Dispatchers can still
  // "Schedule Now" from within the modal by filling date/time/tech.
  const [unscheduledMode, setUnscheduledMode] = useState(false);
  const [unscheduledDraftId, setUnscheduledDraftId] = useState(null);
  // iter110 — Persistent collapsible Draft / Unscheduled rail + Schedule Focus mode.
  // The rail's own collapsed state lives here so the page can also flip both rail +
  // sidebar at once via the Focus button. Both states are remembered in localStorage.
  const [railCollapsed, setRailCollapsed] = useState(() => {
    try { return localStorage.getItem('nexflow.schedule.railCollapsed') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('nexflow.schedule.railCollapsed', railCollapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [railCollapsed]);
  // Focus mode = sidebar AND rail collapsed at once. We compute it from the rail
  // state + a sentinel for the sidebar so the button label stays accurate.
  const [focusMode, setFocusMode] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const toggleFocusMode = useCallback(() => {
    // iter110 — Compute the next value first, then apply state + side-effects sequentially
    // (rather than inside the setFocusMode updater). Avoids React's "setState during render"
    // warning under StrictMode when the updater also dispatches events and flips another state.
    const next = !focusMode;
    setFocusMode(next);
    setRailCollapsed(next);
    window.dispatchEvent(new CustomEvent('nexflow:set-sidebar-collapsed', { detail: { collapsed: next } }));
  }, [focusMode]);
  const [form, setForm] = useState(initialForm());
  const [filters, setFilters] = useState({
    department: '',
    trade_id: '',
    employee_id: '',
    customer_id: '',
    job_type: '',
    status: '',
    truck_id: '',
    location: '',
  });
  const [draggingShift, setDraggingShift] = useState(null);
  const [hoverCell, setHoverCell] = useState(null);   // `${empId}|${date}` or 'unassigned|<date>'
  // iter232 — read-only PTO block details + the PTO assignment-override prompt.
  const [timeOffView, setTimeOffView] = useState(null);
  const [ptoOverride, setPtoOverride] = useState(null); // { message, reason, onConfirm }
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  // iter74 — Edit customer from dispatch shift modal
  const [editCustomerTarget, setEditCustomerTarget] = useState(null);
  // Iter 54: Schedule search
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchStatus, setSearchStatus] = useState('all');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  // Iter 55: searchable customer combobox in shift modal
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerListOpen, setCustomerListOpen] = useState(false);
  // iter62 — Notify Customer confirmation
  const [notifyDialog, setNotifyDialog] = useState({ open: false, mode: null, pendingPayload: null });
  // iter97 Phase 2 — Unscheduled rail drop-scheduling state
  const [unschedDropTarget, setUnschedDropTarget] = useState(null);  // {item, preset}
  // iter106 — Company-wide capacity per day (admin scheduler only).
  const [capacityByDate, setCapacityByDate] = useState({});
  // iter124 Wave M — Move-Up List sheet + count for the toolbar chip.
  const [moveUpSheetOpen, setMoveUpSheetOpen] = useState(false);
  const [moveUpCount, setMoveUpCount] = useState(0);
  // iter167 — Billing Missing safety chip + drill-down sheet.
  const [billingMissing, setBillingMissing] = useState({ count: 0, items: [] });
  const [billingMissingOpen, setBillingMissingOpen] = useState(false);
  // iter125 — Manual day-status overrides (Open/Limited/Full set by dispatch).
  const [dayStatusByDate, setDayStatusByDate] = useState({});
  const [dayStatusEditor, setDayStatusEditor] = useState(null); // { date, status, note } | null
  // iter131 — Duplicate Shift dialog state.
  const [duplicateForm, setDuplicateForm] = useState({ open: false });
  // iter314 — canonical Trade/Department list + Add Visit to Existing Job.
  const [departments, setDepartments] = useState([]);
  const [divisions, setDivisions] = useState([]);   // iter315 — managed Divisions
  const [addVisit, setAddVisit] = useState({ open: false, preset: null });
  // iter142 — Cancel Shift dialog state.
  const [cancelForm, setCancelForm] = useState({ open: false });
  // iter116 — Admin Completion Correction Sheet. Opens from the Edit Shift modal
  // when the shift has a completion_id and the viewer is super_admin/admin/SM.
  const { user } = useAuth();
  const { openAddress: openMapsAddress } = useMapsNav();
  const [editingCompletionId, setEditingCompletionId] = useState(null);
  // 911-fix-iter172 — Invoice group attached to the currently-edited shift so
  // dispatch sees View/Edit Invoice buttons inside the Edit Shift sheet
  // (instead of having to bounce out to /admin/invoices).
  const [editingShiftInvoices, setEditingShiftInvoices] = useState([]);
  useEffect(() => {
    if (!editingShift?.id || !modalOpen) {
      setEditingShiftInvoices([]);
      return;
    }
    let cancelled = false;
    axios.get(`${API}/admin/shift-invoices/${editingShift.id}`, { withCredentials: true })
      .then(({ data }) => { if (!cancelled) setEditingShiftInvoices(Array.isArray(data?.items) ? data.items : []); })
      .catch(() => { if (!cancelled) setEditingShiftInvoices([]); });
    return () => { cancelled = true; };
  }, [editingShift?.id, modalOpen]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deepLinkHandledRef = useRef(null);

  // iter260 — Billing Missing "Open Job" deep-link. /admin/schedule?open=<apptId>
  // fetches the canonical appointment by ID, jumps the calendar to the week that
  // contains its scheduled date, and opens that exact shift drawer. Works for
  // historical / current / future dates, unassigned shifts, and shifts owned by
  // former/inactive employees (never matches by customer name; never mutates).
  // The `open` param is preserved so a browser refresh reopens the same shift.
  // Guarded by the id value (not a boolean) and applies on the fetch success path
  // so it survives React 18 StrictMode double-mount without aborting.
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId || deepLinkHandledRef.current === openId) return;
    deepLinkHandledRef.current = openId;
    (async () => {
      try {
        const { data: appt } = await axios.get(`${API}/admin/appointments/${openId}`, { withCredentials: true });
        if (!appt || !appt.date) {
          toast.error('Could not open that job — it may have been deleted.');
          return;
        }
        // Jump to the week containing the appointment's scheduled date, then open
        // the exact shift drawer once the grid has re-rendered on the new week.
        setWeekStart(getWeekStart(new Date(`${appt.date}T00:00:00`)));
        setTimeout(() => openEditShift({ ...appt, photo_urls: appt.photo_urls || [] }), 300);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Could not open that job.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // iter318 — Dashboard deep link: ?return_action=ID opens the canonical Add Return Visit sheet.
  const returnLinkRef = useRef(null);
  useEffect(() => {
    const rid = searchParams.get('return_action');
    if (!rid || returnLinkRef.current === rid) return;
    returnLinkRef.current = rid;
    axios.get(`${API}/admin/job-return-actions?status=needs_scheduling`, { withCredentials: true })
      .then(({ data }) => {
        const item = (data.actions || []).find((a) => a.id === rid);
        if (!item) { toast.error('That return visit was already scheduled or resolved.'); return; }
        setAddVisit({ open: true, date: (item.promised || item.suggested || {}).date || '', preset: { jobId: item.job_id, returnAction: item } });
      })
      .catch((e) => toast.error(e.response?.data?.detail || 'Could not open that return visit.'));
  }, [searchParams]);


  function initialForm(date) {
    return {
      date: date || toISODate(new Date()),
      start_time: '09:00',
      end_time: '17:00',
      all_day: false,
      shift_title: '',
      department_id: '',
      department: '',
      service_type: '',
      color: '#D4AF37',
      customer_id: '',
      employee_id: '',
      address: '',
      notes: '',
      // iter273 — Property Management linkage (blank for residential/commercial)
      pm_company_id: '',
      property_id: '',
      pm_company_name: '',
      property_name: '',
      // iter276 — separated PM billing (Bill To profile + Requester)
      pm_billing_profile_id: '',
      pm_requester_user_id: '',
      pm_requester_pending_id: '',
      pm_requester_name: '',
      requester_state: '',
      // iter 53 dispatch upgrade
      work_order_number: '',
      office_supplied_po: '',  // iter249 — Office-Supplied PO (separate field from WO#)
      customer_email: '',
      customer_phone: '',  // iter126 — auto-filled from customer record
      dispatcher_notes: '',
      photo_urls: [],
      template_id: '',
      // iter98 Phase 3 — when no employee is assigned, dispatchers can flag the shift
      // iter132 — Visibility & claiming model. Unassigned = automatically claimable.
      // claim_locked is the exception flag (default OFF).
      claim_locked: false,
      // legacy `claimable` kept transient — server now derives it from claim_locked + employee_id
      claimable: true,
      // iter106 — Multi-tech helpers.
      helpers: [],
      // iter220 — Assigned Crew (Phase A): additional crew beyond the Primary/Lead.
      // Each: { employee_id, name, role }. Primary is `employee_id` above.
      crew: [],
      // iter124 Wave M — Move-Up List flag and metadata (customer wants earlier service).
      move_up_requested: false,
      move_up_priority: 'normal',
      move_up_preferred_window: '',
      // iter190 — Real calendar date the customer would take. Preferred over
      // free-text window; when set, dispatch surfaces the job on that date
      // as claimable on the Move-Up rail.
      move_up_date: '',
      move_up_notes: '',
    };
  }

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const startISO = toISODate(weekDays[0]);
  const endISO = toISODate(weekDays[6]);

  useEffect(() => {
    fetchAppointments();
  }, [weekStart, filters, activeDepartment]); // eslint-disable-line

  // iter164 — Removed the legacy iter131 setInterval(15s) here. It was running
  // in PARALLEL with the iter83 `useAutoRefresh` hook below (20s default), so
  // every minute the schedule would refetch up to 7 times — causing visible
  // flicker / capacity-grid blanking. `useAutoRefresh` is the single source of
  // truth for background polling now: tab-visibility aware, edit-aware, and
  // pause-aware while modal/search are open.

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchAppointments = async (opts = {}) => {
    // iter164 — `silent` background refreshes MUST NOT wipe visual layers
    // (capacity grid, day-status pills, move-up chip) when a non-critical
    // sub-fetch transiently fails. The previous code reset each to {} / 0
    // which blanked the grid every refresh cycle if any role-gated sub-endpoint
    // 403'd or transiently 5xx'd.
    const silent = !!opts.silent;
    try {
      const params = { start_date: startISO, end_date: endISO };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      // Department switcher overrides filter.department for super_admin
      // iter318 — global Trade context (records with no Trade stay visible server-side).
      if (activeDepartment && activeDepartment !== 'all' && !filters.trade_id) {
        params.trade_id = activeDepartment;
      }
      const { data } = await axios.get(`${API}/schedule`, { withCredentials: true, params });
      setAppointments(data);
    } catch (err) { console.error(err); }
    // iter106 — Company-wide capacity fetched in parallel; fail soft.
    try {
      const { data } = await axios.get(`${API}/admin/schedule/capacity`, {
        withCredentials: true,
        params: { start_date: startISO, end_date: endISO },
      });
      const byDate = {};
      (data.days || []).forEach((d) => { byDate[d.date] = d; });
      setCapacityByDate(byDate);
    } catch (_err) {
      // iter164 — Keep the previous capacity map during background polls. Only
      // clear on first load (when there's no prior data) to avoid showing
      // stale capacity numbers permanently if the user just lost access.
      if (!silent) setCapacityByDate({});
    }
    // iter124 Wave M — Refresh the Move-Up count chip alongside the schedule fetch.
    try {
      const { data } = await axios.get(`${API}/admin/appointments/move-up-list`, { withCredentials: true });
      setMoveUpCount(Array.isArray(data) ? data.length : 0);
    } catch (_err) {
      // iter164 — keep prior count on silent refresh.
      if (!silent) setMoveUpCount(0);
    }
    // iter125 — Manual day-status overrides for the visible week.
    try {
      const days = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(weekStart); d.setDate(d.getDate() + i); return toISODate(d);
      });
      const startD = days[0], endD = days[6];
      const { data } = await axios.get(
        `${API}/admin/schedule/day-status?start_date=${startD}&end_date=${endD}`,
        { withCredentials: true }
      );
      const map = {};
      (data || []).forEach((row) => { map[row.date] = row; });
      setDayStatusByDate(map);
    } catch (_err) {
      // iter164 — keep prior day-status during silent refresh.
      if (!silent) setDayStatusByDate({});
    }
    // iter167 — Billing Missing diagnostic for the visible week. Lightweight
    // single-query endpoint; tolerates 403 silently for non-admin viewers.
    try {
      const days = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(weekStart); d.setDate(d.getDate() + i); return toISODate(d);
      });
      const { data } = await axios.get(
        `${API}/admin/billing-missing?start_date=${days[0]}&end_date=${days[6]}`,
        { withCredentials: true },
      );
      setBillingMissing({ count: data?.count || 0, items: data?.items || [] });
    } catch (_err) {
      if (!silent) setBillingMissing({ count: 0, items: [] });
    }
  };

  // iter83 — Auto-refresh dispatcher view (20s) paused while shift modal/search is open or
  // dispatcher is editing a field. Manual refresh always available via the indicator button.
  // iter164 — passes { silent: true } so the fetcher keeps prior state on
  // transient sub-fetch failures (capacity / day-status / move-up).
  const refreshAll = useCallback(async (opts = {}) => {
    await fetchAppointments({ silent: opts.silent });
    // iter292 — Keep Unscheduled Jobs fresh on the SAME canonical timer/manual
    // trigger (no second polling system). Quiet reconcile; the rail self-skips
    // if a drag/dialog mutation is in flight so it never clobbers the action.
    if (window.__nexflow_unscheduled_reload_silent) {
      try { await window.__nexflow_unscheduled_reload_silent(); } catch { /* non-blocking */ }
    }
  }, [startISO, endISO, JSON.stringify(filters), activeDepartment]); // eslint-disable-line
  const auto = useAutoRefresh(refreshAll, { pausedByWizard: modalOpen || searchOpen });

  const fetchUsers = async () => {
    try {
      const [c, e, st, jt, dp] = await Promise.all([
        axios.get(`${API}/customers`, { withCredentials: true }),
        axios.get(`${API}/admin/employees`, { withCredentials: true }),
        axios.get(`${API}/service-types`, { withCredentials: true }),
        axios.get(`${API}/job-templates`, { withCredentials: true }),
        axios.get(`${API}/inv/departments`, { withCredentials: true }),
      ]);
      setDepartments(dp.data);
      axios.get(`${API}/divisions`, { withCredentials: true }).then((r) => setDivisions(r.data)).catch(() => {});
      setCustomers(c.data);
      setEmployees(e.data);
      setServiceTypes(st.data);
      setJobTemplates(jt.data);
      // iter256 — Build a full id→employee map INCLUDING inactive/archived so the
      // schedule grid can render rows for shifts assigned to former team members
      // (their historical shifts must never silently disappear from Admin).
      try {
        const all = await axios.get(`${API}/admin/employees?include_inactive=true&include_archived=true`, { withCredentials: true });
        const map = {};
        (all.data || []).forEach((emp) => { map[emp.id] = emp; });
        setAllEmpById(map);
      } catch (_e) { /* fall back to appointment.employee_name */ }
    } catch (err) { console.error(err); }
  };

  // Derived: lookup table for customer-required by service-type name.
  const serviceTypeMap = useMemo(() => {
    const m = {};
    serviceTypes.forEach((s) => { m[s.name] = s; });
    return m;
  }, [serviceTypes]);
  const currentServiceType = serviceTypeMap[form.service_type];
  // iter317 — New Shift cascade: Trade → that Trade's Divisions → that Trade+Division's Service Types.
  const shiftDivisionOpts = useMemo(() => (form.department_id
    ? divisions.filter((d) => d.trade_id === form.department_id && (d.active || d.key === form.department))
    : uniqueDivisionKeys(divisions)), [divisions, form.department_id, form.department]);
  const shiftDivisionId = (divisions.find((d) => d.trade_id === form.department_id && d.key === form.department) || {}).id || '';
  // iter333 — older records store the Division NAME ("Service") instead of its key ("service");
  // resolve it to the exact same Division of the selected Trade (no guessing across Trades).
  useEffect(() => {
    if (!form.department_id || !form.department || shiftDivisionId) return;
    const byName = divisions.find((d) => d.trade_id === form.department_id && (d.name || '').toLowerCase() === String(form.department).toLowerCase());
    if (byName) setForm((f) => ({ ...f, department: byName.key }));
  }, [divisions, form.department_id, form.department, shiftDivisionId]);
  const shiftServiceTypeOpts = useMemo(() => {
    if (!form.department_id) return serviceTypes;
    const mapped = shiftDivisionId ? serviceTypes.filter((s) => s.department_id === form.department_id && s.division_id === shiftDivisionId) : [];
    if (unscheduledMode) return mapped;
    const legacy = serviceTypes.filter((s) => !s.division_id && (!s.department_id || s.department_id === form.department_id));
    return [...mapped, ...legacy];
  }, [serviceTypes, form.department_id, shiftDivisionId, unscheduledMode]);
  const currentRequiresCustomer = currentServiceType ? currentServiceType.customer_required : true;
  const mappedServiceType = useMemo(() => (form.department_id && shiftDivisionId
    ? serviceTypes.find((s) => s.name === form.service_type && s.department_id === form.department_id && s.division_id === shiftDivisionId)
    : null) || null, [serviceTypes, form.service_type, form.department_id, shiftDivisionId]);
  // What the historical Unscheduled record originally said (reference only — never remapped).
  const [unschedOriginal, setUnschedOriginal] = useState(null);
  // iter316 — what an Unscheduled Job still needs before Schedule Now (tech optional = Unassigned).
  const unschedMissing = useMemo(() => {
    const m = [];
    if (!form.department_id) m.push('Trade');
    if (!form.department) m.push('Division');
    if (!form.service_type) m.push('Service Type');
    else if (!mappedServiceType) m.push('Service Type mapped to this Trade + Division');
    if (!form.date) m.push('Date');
    if (!form.all_day && !(form.start_time && form.end_time)) m.push('Start/End time');
    if (currentRequiresCustomer !== false && !form.customer_id) m.push('Customer');
    return m;
  }, [form.department_id, form.department, form.service_type, form.date, form.all_day, form.start_time, form.end_time, form.customer_id, currentRequiresCustomer, mappedServiceType]);

  // iter273 — customer picker is now server-backed (handleAccountResult); the old
  // client-side customerMatches memo was removed.

  // iter273 — Unified Service Account search (PM Company / PM Property / Customer).
  // Server-backed so PM companies + properties are findable, not just db.users contacts.
  const [acctResults, setAcctResults] = useState([]);
  const [acctLoading, setAcctLoading] = useState(false);
  const [pmPropertyPicker, setPmPropertyPicker] = useState(null); // { company, properties } when a PM company is chosen
  const [addProp, setAddProp] = useState(null); // inline "Add Property" form: null | { name, address, confirmed }
  const [addPropSaving, setAddPropSaving] = useState(false);
  const [pmBillingCtx, setPmBillingCtx] = useState(null); // iter276 { profiles, requesters, default_billing_profile_id }
  const loadPmBillingContext = async (companyId) => {
    if (!companyId) { setPmBillingCtx(null); return; }
    try {
      const { data } = await axios.get(`${API}/schedule/pm-company/${companyId}/billing-context`, { withCredentials: true });
      setPmBillingCtx(data);
      return data;
    } catch { setPmBillingCtx(null); }
  };
  useEffect(() => {
    const q = customerSearch.trim();
    if (!q) { setAcctResults([]); return; }
    let cancelled = false;
    setAcctLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/schedule/account-search`, { params: { q }, withCredentials: true });
        if (!cancelled) setAcctResults(data.results || []);
      } catch { if (!cancelled) setAcctResults([]); }
      finally { if (!cancelled) setAcctLoading(false); }
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [customerSearch]);

  const applyTemplate = (template) => {
    if (!template) return;
    setForm((prev) => ({
      ...prev,
      template_id: template.id,
      service_type: prev.service_type || template.service_type || '',
      color: prev.color === '#D4AF37' ? COLOR_FOR_NAME(template.service_type) : prev.color,
      dispatcher_notes: prev.dispatcher_notes || template.dispatcher_notes || '',
      notes: prev.notes || ((template.checklist || []).length > 0
        ? `Checklist:\n- ${(template.checklist || []).join('\n- ')}`
        : prev.notes),
    }));
  };

  // Photo upload (base64 data URL — same pattern used elsewhere in the app).
  const handlePhotoUpload = async (files) => {
    const fileArr = Array.from(files || []);
    for (const f of fileArr) {
      if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name}: too large (8MB max)`); continue; }
      const reader = new FileReader();
      reader.onload = (ev) => {
        setForm((prev) => ({ ...prev, photo_urls: [...(prev.photo_urls || []), ev.target.result] }));
      };
      reader.readAsDataURL(f);
    }
  };
  const removePhoto = (idx) => setForm((prev) => ({ ...prev, photo_urls: prev.photo_urls.filter((_, i) => i !== idx) }));

  // Iter 54: Schedule search — debounced on Enter / button click
  const runSearch = async (qOverride) => {
    const q = (qOverride ?? searchQ).trim();
    if (!q && searchStatus === 'all') { setSearchResults([]); return; }
    setSearchLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (searchStatus !== 'all') params.set('status', searchStatus);
      params.set('limit', '50');
      const { data } = await axios.get(`${API}/admin/schedule/search?${params.toString()}`, { withCredentials: true });
      setSearchResults(data.results || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Search failed');
    }
    setSearchLoading(false);
  };
  const openShiftFromSearch = (row) => {
    setSearchOpen(false);
    // Jump the calendar to that week + open detail
    const d = new Date(row.date);
    setWeekStart(getWeekStart(d));
    setTimeout(() => openEditShift({ ...row, photo_urls: row.photo_urls || [] }), 200);
  };

  const openAddShift = (presetDate, presetEmployeeId) => {
    setUnschedOriginal(null);
    const f = initialForm(presetDate);
    if (presetEmployeeId) f.employee_id = presetEmployeeId;
    setForm(f);
    setEditingShift(null);
    setUnscheduledMode(false);
    setUnscheduledDraftId(null);
    setModalOpen(true);
  };

  // iter188 — Open the same New Shift modal in "unscheduled mode": date, time
  // and employee are pre-cleared, the primary Save button routes to
  // persistDraft (Holding Area), and dispatch can optionally fill date+time+tech
  // and click "Schedule Now" to publish directly.
  const openAddUnscheduled = () => {
    setUnschedOriginal(null);
    const f = initialForm();
    f.date = '';
    f.start_time = '';
    f.end_time = '';
    f.employee_id = '';
    setForm(f);
    setEditingShift(null);
    setUnscheduledMode(true);
    setUnscheduledDraftId(null);
    setModalOpen(true);
  };

  // iter188 — Open the same New Shift modal to edit an existing unscheduled
  // draft. Carries every saved field over (customer, notes, photos, work
  // order #, HSP, etc.) so nothing is lost when converting to scheduled.
  const withQuickTimes = (it, qs) => ({ ...it, requested_date: qs?.date || it.requested_date,
    requested_time: qs?.start_time || it.requested_time, requested_time_end: qs?.end_time || it.requested_time_end });
  const openEditUnscheduled = (draft) => {
    // Legacy drafts may lack Trade/Division — never guess; the drawer flags them as required
    // and keeps the original values visible as reference.
    setUnschedOriginal({ service_type: draft.service_type || '', department: draft.department || '', department_id: draft.department_id || '' });
    if (draft.pm_company_id) loadPmBillingContext(draft.pm_company_id); else setPmBillingCtx(null);
    setForm({
      date: draft.requested_date || '',
      start_time: draft.requested_time || '',
      end_time: draft.requested_time_end || '',
      all_day: !!draft.all_day,
      shift_title: draft.title || '',
      service_type: draft.service_type || '',
      department_id: draft.department_id || '',
      department: draft.department || '',
      pm_company_id: draft.pm_company_id || '',
      property_id: draft.property_id || '',
      pm_billing_profile_id: draft.pm_billing_profile_id || '',
      pm_requester_user_id: draft.pm_requester_user_id || '',
      pm_requester_pending_id: draft.pm_requester_pending_id || '',
      pm_requester_name: draft.pm_requester_name || '',
      requester_state: draft.requester_state || '',
      job_number: draft.job_number || '',
      color: draft.color || '#D4AF37',
      customer_id: draft.customer_id || '',
      employee_id: '',
      address: draft.address || '',
      notes: draft.notes || '',
      work_order_number: draft.work_order_number || '',
      office_supplied_po: draft.office_supplied_po || '',  // iter249
      customer_email: draft.customer_email || '',
      customer_phone: draft.customer_phone || '',
      dispatcher_notes: draft.dispatcher_notes || '',
      photo_urls: draft.photo_urls || [],
      template_id: draft.job_template_id || '',
      helpers: draft.helpers || [],
      claimable: false,
      claim_locked: false,
      move_up_requested: false,
      move_up_priority: 'normal',
      move_up_preferred_window: '',
      move_up_date: '',
      move_up_notes: '',
    });
    setEditingShift(null);
    setUnscheduledMode(true);
    setUnscheduledDraftId(draft.id);
    setModalOpen(true);
  };

  const openAddTimeOff = (presetDate, presetEmployeeId) => {
    setTimeOffForm({
      ...timeOffInitial(),
      start_date: presetDate || toISODate(new Date()),
      employee_id: presetEmployeeId || '',
    });
    setTimeOffOpen(true);
  };

  const submitTimeOff = async (withOverride = false) => {
    if (!timeOffForm.employee_id) { toast.error('Pick an employee'); return; }
    if (!timeOffForm.start_date) { toast.error('Start date required'); return; }
    const body = {
      employee_id: timeOffForm.employee_id,
      start_date: timeOffForm.start_date,
      end_date: timeOffForm.end_date || timeOffForm.start_date,
      time_off_type: timeOffForm.time_off_type,
      reason: timeOffForm.reason,
    };
    if (withOverride) {
      body.override = true;
      body.override_reason = (timeOffForm.override_reason || '').trim() || 'Scheduled over conflict by dispatch';
    }
    setTimeOffSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/timeoff/schedule`, body, { withCredentials: true });
      const label = timeOffForm.time_off_type === 'unpaid' ? 'Unpaid Time Off' : 'Paid PTO';
      toast.success(`${label} scheduled${typeof data.total_paid_hours === 'number' ? ` · ${data.total_paid_hours.toFixed(1)} paid h` : ''}`);
      setTimeOffOpen(false);
      refreshAll();
    } catch (e) {
      const detail = (!Array.isArray(e.response?.data?._detailMeta) && e.response?.data?._detailMeta) || e.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'TIME_OFF_CONFLICT') {
        if (user?.role === 'super_admin') {
          if (window.confirm(`${detail.message}\n\nOverride and schedule the time off anyway?`)) {
            await submitTimeOff(true);
          }
        } else {
          toast.error(detail.message || 'This employee is already scheduled on one or more of these days.');
        }
      } else if (detail && typeof detail === 'object') {
        toast.error(detail.message || 'Could not schedule time off');
      } else {
        toast.error(detail || 'Could not schedule time off');
      }
    }
    setTimeOffSaving(false);
  };

  const openAddRecurring = () => {
    setRecForm(recInitial());
    setRecurringOpen(true);
  };

  const submitRecurring = async () => {
    if (!recForm.service_type) { toast.error('Pick a service type / activity'); return; }
    if (!recForm.start_date) { toast.error('Start date required'); return; }
    if (recForm.end_mode === 'date' && !recForm.end_date) { toast.error('End date required'); return; }
    if (recForm.end_mode === 'count' && !recForm.occurrences) { toast.error('Number of occurrences required'); return; }
    const crewOut = [];
    if (recForm.employee_id) crewOut.push({ employee_id: recForm.employee_id, role: 'primary_lead', is_primary: true });
    (recForm.crew || []).forEach((c) => { if (c.employee_id && c.employee_id !== recForm.employee_id) crewOut.push({ employee_id: c.employee_id, role: c.role || 'assigned_technician', is_primary: false }); });
    const body = {
      start_date: recForm.start_date,
      end_date: recForm.end_mode === 'date' ? recForm.end_date : null,
      occurrences: recForm.end_mode === 'count' ? Number(recForm.occurrences) : null,
      start_time: recForm.start_time, end_time: recForm.end_time, repeat: recForm.repeat,
      weekdays: recForm.repeat === 'daily' ? [] : recForm.weekdays,
      service_type: recForm.service_type, shift_title: recForm.shift_title,
      customer_id: recForm.customer_id || null, work_order_number: recForm.work_order_number,
      dispatcher_notes: recForm.dispatcher_notes, crew_assignments: crewOut.length ? crewOut : null,
      is_internal_activity: !!recForm.is_internal_activity, status: recForm.status,
    };
    setRecSaving(true);
    try {
      const { data } = await axios.post(`${API}/schedule/recurring`, body, { withCredentials: true });
      toast.success(`Created ${data.occurrences_created} recurring occurrences`);
      setRecurringOpen(false);
      refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create recurring series'); }
    setRecSaving(false);
  };

  const openEditShift = (shift) => {
    // iter232 — Time-off blocks are not editable jobs. Open a read-only detail view.
    if (shift.is_time_off_block) {
      setTimeOffView(shift);
      return;
    }
    setEditingShift(shift);
    setUnscheduledMode(false);
    setUnscheduledDraftId(null);
    if (shift.pm_company_id) loadPmBillingContext(shift.pm_company_id); else setPmBillingCtx(null);
    setForm({
      date: shift.date,
      start_time: shift.start_time || '09:00',
      end_time: shift.end_time || '17:00',
      all_day: shift.all_day || false,
      shift_title: shift.shift_title || '',
      department_id: shift.department_id || '',
      department: shift.department || '',
      service_type: shift.service_type || '',
      color: shift.color || '#D4AF37',
      customer_id: shift.customer_id || '',
      employee_id: shift.employee_id || '',
      address: shift.address || '',
      notes: shift.notes || '',
      // iter273 — restore PM linkage on reopen
      pm_company_id: shift.pm_company_id || '',
      property_id: shift.property_id || '',
      pm_company_name: shift.pm_company_name || '',
      property_name: shift.property_name || '',
      // iter276 — restore separated billing selections
      pm_billing_profile_id: shift.pm_billing_profile_id || '',
      pm_requester_user_id: shift.pm_requester_user_id || '',
      pm_requester_pending_id: shift.pm_requester_pending_id || '',
      pm_requester_name: shift.pm_requester_name || '',
      requester_state: shift.requester_state || '',
      work_order_number: shift.work_order_number || '',
      office_supplied_po: shift.office_supplied_po || '',  // iter249 — restore Office PO on reopen (was dropped)
      customer_email: shift.customer_email || '',
      customer_phone: shift.customer_phone || '',  // iter126
      dispatcher_notes: shift.dispatcher_notes || '',
      photo_urls: shift.photo_urls || [],
      template_id: shift.template_id || '',
      claimable: !!shift.claimable,
      claim_locked: !!shift.claim_locked,  // iter132
      helpers: shift.helpers || [],
      // iter220 — Assigned Crew: hydrate extras from crew_assignments (preferred) or legacy helpers.
      crew: (shift.crew_assignments && shift.crew_assignments.length)
        ? shift.crew_assignments.filter((c) => !c.is_primary).map((c) => ({ employee_id: c.employee_id, name: c.name, role: c.role || 'assigned_technician' }))
        : (shift.helpers || []).map((h) => ({ employee_id: h.employee_id, name: h.name, role: 'helper' })),
      // iter124 Wave M — Move-Up List
      move_up_requested: !!shift.move_up_requested,
      move_up_priority: shift.move_up_priority || 'normal',
      move_up_preferred_window: shift.move_up_preferred_window || '',
      move_up_date: shift.move_up_date || '',
      move_up_notes: shift.move_up_notes || '',
    });
    setModalOpen(true);
  };

  const handleSave = async (asDraft = false) => {
    // iter273 — PM hierarchy guard: a PM company must resolve to a specific Property
    // (service location) before publishing. Drafts may be saved without it.
    if (!asDraft && form.pm_company_id && !form.property_id) {
      toast.error('Select a specific Property (service location) for this Property Management company before publishing.');
      return;
    }
    // Requester is REQUIRED for PM jobs (an intentional state — including
    // "Unknown / Not Provided"). Never blocks: dispatcher can add an unlisted
    // requester or explicitly mark unknown right in the picker.
    if (!asDraft && form.pm_company_id) {
      const hasRequester = form.pm_requester_user_id || form.pm_requester_pending_id || form.requester_state === 'unknown';
      if (!hasRequester) {
        toast.error('Select a requester for this PM job (search, add unlisted, or mark "Unknown / Not Provided").');
        return;
      }
    }
    // iter316 — Scheduling an Unscheduled Job: every currently-required field must be present.
    if (!asDraft && unscheduledMode && unschedMissing.length) {
      toast.error(`Missing to schedule: ${unschedMissing.join(', ')}`);
      return;
    }
    // iter314 — Department → Service Type is required for new shifts.
    if (!asDraft && !editingShift && !unscheduledMode && !form.department_id) {
      toast.error('Select a Trade first, then the Service Type.');
      return;
    }
    if (!asDraft && !editingShift && !unscheduledMode && !form.department) {
      toast.error('Select a Division (Service / New Construction / Office).');
      return;
    }
    const payload = { ...form, status: asDraft ? 'draft' : 'scheduled' };
    delete payload.job_number;
    if (!asDraft && unscheduledMode && unscheduledDraftId) payload.from_unscheduled_draft_id = unscheduledDraftId;
    if (!asDraft && unscheduledMode && mappedServiceType) payload.service_type_id = mappedServiceType.id;
    if (!payload.department_id) delete payload.department_id;
    // Strip UI-only display fields (backend derives names).
    delete payload.pm_company_name;
    delete payload.property_name;
    // iter220 — Assigned Crew. Compose canonical crew_assignments (primary + extras).
    // Backend sets employee_id from the primary and mirrors extras into helpers.
    const crewOut = [];
    if (form.employee_id) crewOut.push({ employee_id: form.employee_id, role: 'primary_lead', is_primary: true });
    (form.crew || []).forEach((c) => {
      if (c.employee_id && c.employee_id !== form.employee_id) {
        crewOut.push({ employee_id: c.employee_id, role: c.role || 'assigned_technician', is_primary: false });
      }
    });
    if (crewOut.length) payload.crew_assignments = crewOut;
    else delete payload.crew_assignments;
    // Keep helpers populated for draft/legacy paths (compat bridge).
    payload.helpers = (form.crew || []).map((c) => ({ employee_id: c.employee_id, name: c.name, role: 'helper' }));
    delete payload.crew;
    if (!payload.customer_id) delete payload.customer_id;
    if (!payload.employee_id) delete payload.employee_id;
    if (!payload.template_id) delete payload.template_id;

    // iter166 — Never save UI placeholder strings as real customer data. If the
    // dispatcher left `555-555-5555` or `customer@email.com` in the form, blank
    // them out so the shift doesn't carry junk into invoices / SMS / portal.
    if (isPlaceholder(payload.customer_phone, 'phone')) payload.customer_phone = '';
    if (isPlaceholder(payload.customer_email, 'email')) payload.customer_email = '';

    // iter188 — Unscheduled mode. Primary "Save Unscheduled Job" routes to the
    // Holding Area regardless of whether date/time/tech are filled. The
    // secondary "Schedule Now" button uses the normal publish flow (asDraft
    // will be false, and unscheduledMode is momentarily bypassed via
    // schedulingFromUnscheduled below).
    if (unscheduledMode && asDraft) {
      await persistDraft(payload, unscheduledDraftId);
      return;
    }

    // ─── iter108 Save-as-Draft → persistent Unscheduled holding area ───
    // "Draft" no longer means "appointment with status=draft" (which was week-locked and
    // disappeared on week change). It now POSTs to /api/admin/unscheduled-drafts so the
    // job lives in the persistent rail until dispatch is ready to schedule it.
    // Edit-an-existing-appointment still uses the old PUT /schedule path (the Draft
    // button is hidden in that mode — only Publish remains).
    if (asDraft) {
      await persistDraft(payload);
      return;
    }
    // No customer attached → no comms can be sent. Just save.
    if (!form.customer_id) {
      await persistShift(payload, asDraft);
      return;
    }
    // First publish detection: creating a new shift (no editingShift) OR editing a draft → published.
    const isFirstPublish = !editingShift || editingShift.status === 'draft';
    if (isFirstPublish) {
      setNotifyDialog({ open: true, mode: 'publish_first', pendingPayload: payload });
      return;
    }
    // Editing an already-published shift: check if any customer-facing field changed.
    const customerFacingChanged = ['date', 'start_time', 'end_time', 'address']
      .some((k) => (form[k] || '') !== (editingShift[k] || ''));
    if (customerFacingChanged) {
      setNotifyDialog({ open: true, mode: 'reschedule', pendingPayload: payload });
      return;
    }
    // Pure internal edit → save silently, no flag.
    await persistShift(payload, asDraft);
  };

  // iter108 — Persist a draft into the persistent Unscheduled rail (NOT db.appointments).
  // The user clicks "Draft" on the New Shift form when the job isn't fully scheduled yet —
  // missing tech, missing confirmed date, waiting on info, etc.
  // iter188 — Also used by the new "Save Unscheduled Job" primary button. When
  // draftId is passed we PUT to update the existing draft instead of creating
  // a new one — this is how dispatch edits a card without losing history.
  const persistDraft = async (payload, draftId = null) => {
    try {
      const draftBody = {
        title: payload.shift_title || payload.service_type || 'Unscheduled job',
        customer_id: payload.customer_id || null,
        customer_email: payload.customer_email || '',
        customer_phone: payload.customer_phone || '',
        address: payload.address || '',
        notes: payload.notes || '',
        priority: 'Normal',
        service_type: payload.service_type || '',
        work_order_number: payload.work_order_number || '',
        office_supplied_po: payload.office_supplied_po || '',
        department: payload.department || '',
        dispatcher_notes: payload.dispatcher_notes || '',
        job_template_id: payload.template_id || '',
        requested_date: payload.date || '',
        requested_time: payload.start_time || '',
        requested_time_end: payload.end_time || '',
        holding_status: 'new',
        helpers: payload.helpers || [],
        color: payload.color || '',
        photo_urls: payload.photo_urls || [],
        department_id: payload.department_id || '',
        all_day: !!payload.all_day,
        pm_company_id: payload.pm_company_id || '',
        property_id: payload.property_id || '',
        pm_billing_profile_id: payload.pm_billing_profile_id || '',
        pm_requester_user_id: payload.pm_requester_user_id || '',
        pm_requester_pending_id: payload.pm_requester_pending_id || '',
        pm_requester_name: payload.pm_requester_name || '',
        requester_state: payload.requester_state || '',
      };
      if (draftId) {
        await axios.put(`${API}/admin/unscheduled-drafts/${draftId}`, draftBody, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/unscheduled-drafts`, draftBody, { withCredentials: true });
      }
      toast.success('Saved to Unscheduled rail');
      setModalOpen(false);
      fetchAppointments();
      // Refresh the rail too so the new card pops in immediately.
      if (window.__nexflow_unscheduled_reload) window.__nexflow_unscheduled_reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save draft');
    }
  };

  const persistShift = async (payload, asDraft) => {
    try {
      if (editingShift) {
        // iter131 — Optimistic concurrency: send the updated_at we loaded the
        // drawer with. If someone else changed the shift since, the server
        // returns 409 with `code: "SHIFT_STALE"` + the latest doc so we can
        // ask the user to reload before clobbering their teammate's edit.
        const qs = editingShift.updated_at ? `?expected_updated_at=${encodeURIComponent(editingShift.updated_at)}` : '';
        await axios.put(`${API}/schedule/${editingShift.id}${qs}`, payload, { withCredentials: true });
        toast.success(asDraft ? 'Saved as draft' : 'Shift updated');
      } else {
        await axios.post(`${API}/schedule`, payload, { withCredentials: true });
        toast.success(asDraft ? 'Draft saved' : 'Shift published');
      }
      setModalOpen(false);
      fetchAppointments();
      if (payload.from_unscheduled_draft_id && window.__nexflow_unscheduled_reload) window.__nexflow_unscheduled_reload();
    } catch (err) {
      // iter131 — Special-case SHIFT_STALE so the dispatcher can pick: reload latest or keep editing.
      const detail = (!Array.isArray(err.response?.data?._detailMeta) && err.response?.data?._detailMeta) || err.response?.data?.detail;
      if (err.response?.status === 409 && detail && detail.code === 'SHIFT_STALE') {
        const reload = window.confirm(
          `${detail.message}\n\nOK = Reload latest (your unsaved typing on this drawer will be replaced).\nCancel = Keep editing (save again later — you'll be re-warned).`
        );
        if (reload && detail.latest) {
          // Refresh the schedule and reopen the drawer with the latest values.
          await fetchAppointments();
          setEditingShift(detail.latest);
          // Reopen with latest values — same logic as openEditShift but in-place.
          const s = detail.latest;
          setForm((p) => ({
            ...p,
            customer_id: s.customer_id || '',
            employee_id: s.employee_id || '',
            date: s.date || p.date,
            start_time: s.start_time || p.start_time,
            end_time: s.end_time || p.end_time,
            service_type: s.service_type || p.service_type,
            shift_title: s.shift_title || '',
            address: s.address || '',
            notes: s.notes || '',
            color: s.color || p.color,
            all_day: !!s.all_day,
            tags: s.tags || [],
            work_order_number: s.work_order_number || '',
            customer_email: s.customer_email || '',
            customer_phone: s.customer_phone || '',
            dispatcher_notes: s.dispatcher_notes || '',
            photo_urls: s.photo_urls || [],
            template_id: s.template_id || '',
            claimable: !!s.claimable,
            claim_locked: !!s.claim_locked,
            helpers: s.helpers || [],
            move_up_requested: !!s.move_up_requested,
            move_up_priority: s.move_up_priority || 'normal',
            move_up_preferred_window: s.move_up_preferred_window || '',
            move_up_date: s.move_up_date || '',
            move_up_notes: s.move_up_notes || '',
          }));
        }
        return;
      }
      // iter275 — SOFT PTO/Holiday scheduling warning. ONE combined confirm dialog;
      // dispatch is never hard-blocked. Continue → retry with the ack flag.
      if (err.response?.status === 409 && detail && detail.code === 'SCHEDULE_WARNING') {
        const proceed = window.confirm(`${detail.message}\n\nOK = Continue scheduling\nCancel = Stop`);
        if (proceed) await persistShift({ ...payload, override_schedule_warnings: true }, asDraft);
        return;
      }
      toast.error((detail && typeof detail === 'object' ? detail.message : detail) || 'Failed to save');
    }
  };

  const handleNotifyDialogConfirm = async (notifyCustomer) => {
    const { mode, pendingPayload } = notifyDialog;
    setNotifyDialog({ open: false, mode: null, pendingPayload: null });
    if (!pendingPayload) return;
    const enriched = { ...pendingPayload, notify_customer: notifyCustomer };
    if (mode === 'reschedule') enriched.notify_type = 'reschedule';
    await persistShift(enriched, false);
  };

  const handleNotifyDialogCancel = () => {
    setNotifyDialog({ open: false, mode: null, pendingPayload: null });
  };

  // Manual resend buttons inside the shift drawer (visible only when editing a published shift).
  const handleManualNotify = async (type) => {
    if (!editingShift) return;
    try {
      const { data } = await axios.post(`${API}/schedule/${editingShift.id}/notify`, { type }, { withCredentials: true });
      if (data?.result?.sent) toast.success(`${type === 'confirmation' ? 'Confirmation' : 'Schedule update'} sent`);
      else if (data?.result?.reason === 'user_opted_out') toast.warning('Customer has opted out — skipped.');
      else if (data?.result?.reason === 'sms_globally_disabled') toast.warning('Global SMS is disabled — skipped.');
      else if (data?.result?.fallback) toast.warning('Twilio not configured — logged only.');
      else toast.success('Notification queued');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    }
  };

  // When the dispatcher picks a customer, auto-fill email, phone, and address if the
  // customer record has them. Per-shift edits still win — we only fill when blank.
  // iter166 — Accept an optional `customer` object so the freshly-created customer
  // from QuickAddCustomerDialog autofills immediately (the `customers` state hasn't
  // re-rendered yet at the moment we call this helper from `onCreated`).
  // iter166 — Also treat well-known placeholder strings as "blank" so they don't
  // win against a real customer record (e.g. a tech who left `555-555-5555` or
  // `customer@email.com` in the field).
  const PLACEHOLDER_PHONES = new Set(['555-555-5555', '5555555555', '(555) 555-5555']);
  const PLACEHOLDER_EMAILS = new Set(['customer@email.com']);
  const isPlaceholder = (val, kind) => {
    if (!val) return true;
    const v = String(val).trim().toLowerCase();
    if (!v) return true;
    if (kind === 'phone') return PLACEHOLDER_PHONES.has(v.replace(/\s/g, ''));
    if (kind === 'email') return PLACEHOLDER_EMAILS.has(v);
    return false;
  };

  const handlePickCustomer = async (customerId, customerObj) => {
    if (!customerId) {
      setForm((p) => ({ ...p, customer_id: '', customer_email: '', customer_phone: '', address: '', pm_company_id: '', property_id: '', pm_company_name: '', property_name: '' }));
      return;
    }
    // iter251 — Canonical customer email/phone/address ALWAYS win on pick. We clear
    // the previously-selected customer's contact fields and hydrate the new one from
    // the saved record, never from stale local state or a placeholder. A missing
    // email stays BLANK (the grey placeholder is display-only, never saved as data).
    const hydrate = (c) => {
      const realEmail = c.email && !String(c.email).endsWith('@noemail.local') ? c.email : '';
      setForm((p) => ({
        ...p,
        customer_id: customerId,
        customer_email: realEmail,
        customer_phone: c.phone || '',
        address: c.address || '',
      }));
    };
    // 1) Immediate hydrate from whatever object we already have (dropdown row or the
    //    fresh QuickAdd create response) so the UI updates instantly.
    const local = customerObj || customers.find((c) => c.id === customerId);
    if (local) hydrate(local);
    else setForm((p) => ({ ...p, customer_id: customerId, customer_email: '', customer_phone: '', address: '' }));
    // Picking a plain customer clears any prior PM linkage.
    setForm((p) => ({ ...p, pm_company_id: '', property_id: '', pm_company_name: '', property_name: '', pm_billing_profile_id: '', pm_requester_user_id: '', pm_requester_pending_id: '', pm_requester_name: '', requester_state: '' }));
    setPmBillingCtx(null);
    // 2) Refetch the canonical record and override — defeats a stale customers list.
    try {
      const { data } = await axios.get(`${API}/admin/customers/${customerId}`, { withCredentials: true });
      if (data && data.id) {
        hydrate(data);
        setCustomers((prev) => prev.map((p) => (p.id === data.id ? { ...p, ...data } : p)));
      }
    } catch { /* keep the local hydrate if the canonical fetch fails */ }
  };

  // iter273 — route a unified account-search result to the right flow.
  const handleAccountResult = async (r) => {
    if (r.kind === 'customer') {
      handlePickCustomer(r.id, { id: r.id, name: r.name, email: r.email, phone: r.phone, address: r.address });
      setCustomerSearch(''); setCustomerListOpen(false); setAcctResults([]);
      return;
    }
    if (r.kind === 'pm_company') {
      try {
        const { data } = await axios.get(`${API}/schedule/pm-company/${r.id}/properties`, { withCredentials: true });
        setCustomerListOpen(false);
        setPmPropertyPicker({ company: data.company, properties: data.properties });
      } catch (e) { toast.error(e.response?.data?.detail || 'Could not load properties'); }
      return;
    }
    if (r.kind === 'pm_property') {
      await selectPmProperty(r.id);
      setCustomerSearch(''); setCustomerListOpen(false); setAcctResults([]);
    }
  };

  const selectPmProperty = async (propertyId) => {
    // Inline "Add Property / Service Location" during scheduling — reuses the
    // canonical PM property system, checks for duplicate CHN addresses first,
    // then returns to the shift and auto-selects the new property.
    const createPropertyInline = async () => {
      const name = (addProp?.name || '').trim();
      const address = (addProp?.address || '').trim();
      if (!name && !address) { toast.error('Enter a property name or address'); return; }
      const companyId = pmPropertyPicker?.company?.id;
      if (!companyId) { toast.error('No PM company selected'); return; }
      if (!addProp?.confirmed && !((addProp?.dups || []).length > 0)) {
        const key = (address || name).toLowerCase().replace(/\s+/g, ' ').trim();
        const dups = (pmPropertyPicker.properties || []).filter((p) => {
          const pa = (p.address || '').toLowerCase(); const pn = (p.name || '').toLowerCase();
          return (key && (pa.includes(key) || key.includes(pa) && pa)) || (name && pn === name.toLowerCase());
        });
        if (dups.length > 0) { setAddProp((f) => ({ ...f, dups })); return; }
      }
      setAddPropSaving(true);
      try {
        const { data } = await axios.post(`${API}/admin/pm/properties`,
          { company_id: companyId, name: name || address, address }, { withCredentials: true });
        toast.success(`Property added: ${data.name || name}`);
        setAddProp(null);
        await selectPmProperty(data.id);
      } catch (e) {
        const detail = e.response?.data?.detail;
        const msg = Array.isArray(detail) ? (detail[0]?.msg || 'Invalid property data') : (detail || 'Could not create property');
        toast.error(msg);
      } finally { setAddPropSaving(false); }
    };
    if (propertyId === '__inline_create__') { await createPropertyInline(); return; }
    try {
      const { data } = await axios.post(`${API}/schedule/select-pm-property`, { property_id: propertyId }, { withCredentials: true });
      setForm((p) => ({
        ...p,
        customer_id: data.customer.id,
        customer_email: data.customer.email || '',
        customer_phone: data.customer.phone || '',
        address: data.address || data.customer.address || '',
        pm_company_id: data.pm_company_id || '',
        property_id: data.property_id || '',
        pm_company_name: data.pm_company_name || '',
        property_name: data.property_name || '',
      }));
      setPmPropertyPicker(null);
      toast.success(`${data.pm_company_name} → ${data.property_name} (Property Management)`);
      // iter276 — load billing profiles + requesters; default Bill To = Main AP.
      const ctx = await loadPmBillingContext(data.pm_company_id);
      if (ctx) setForm((p) => ({ ...p, pm_billing_profile_id: ctx.default_billing_profile_id || '', pm_requester_user_id: '', pm_requester_pending_id: '', pm_requester_name: '', requester_state: '' }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not select property'); }
  };

  const cancelSeries = async (scope) => {
    if (!editingShift?.series_id) return;
    const reason = window.prompt('Cancellation reason (required):') || '';
    if (!reason.trim()) { toast.error('A reason is required'); return; }
    try {
      const { data } = await axios.post(`${API}/schedule/series/${editingShift.series_id}/cancel`, {
        scope, occurrence_id: editingShift.id, from_date: editingShift.date, reason,
      }, { withCredentials: true });
      toast.success(`Canceled ${data.canceled} occurrence(s)`);
      setModalOpen(false);
      refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cancel failed'); }
  };

  const handleDelete = async () => {
    if (!editingShift) return;
    try {
      await axios.delete(`${API}/schedule/${editingShift.id}`, { withCredentials: true });
      toast.success('Shift deleted');
      setModalOpen(false);
      fetchAppointments();
    } catch { toast.error('Failed'); }
  };

  // ── Drag & drop: reschedule a shift to a new (employee, date) cell ──
  const moveShift = async (shift, newEmployeeId, newDate, overrideFields = null) => {
    const sameSlot = (shift.employee_id || '') === (newEmployeeId || '') && shift.date === newDate;
    if (sameSlot) return;
    // Optimistic: update local state first so it feels instant.
    setAppointments((prev) => prev.map((a) =>
      a.id === shift.id
        ? { ...a, employee_id: newEmployeeId || null, employee_name: newEmployeeId ? (employees.find(e => e.id === newEmployeeId)?.name || '') : '', date: newDate }
        : a
    ));
    try {
      await axios.put(`${API}/schedule/${shift.id}`, {
        date: newDate,
        employee_id: newEmployeeId || '',
        ...(overrideFields || {}),
      }, { withCredentials: true });
      toast.success(newEmployeeId ? `Moved to ${employees.find(e => e.id === newEmployeeId)?.name || 'tech'} on ${newDate}` : `Moved to Unassigned • ${newDate}`);
      fetchAppointments();   // re-sync from server for canonical state
    } catch (err) {
      const detail = (!Array.isArray(err.response?.data?._detailMeta) && err.response?.data?._detailMeta) || err.response?.data?.detail;
      // iter275 — Moving a job onto a PTO/holiday day → soft combined warning.
      if (err.response?.status === 409 && detail && detail.code === 'SCHEDULE_WARNING') {
        fetchAppointments(); // revert optimistic move until confirmed
        const proceed = window.confirm(`${detail.message}\n\nOK = Continue scheduling\nCancel = Stop`);
        if (proceed) await moveShift(shift, newEmployeeId, newDate, { override_schedule_warnings: true });
        return;
      }
      toast.error((detail && typeof detail === 'object' ? detail.message : detail) || 'Failed to move shift');
      fetchAppointments();   // revert by re-fetching
    }
  };

  const handleCellDrop = (e, empId, date) => {
    e.preventDefault();
    setHoverCell(null);
    // iter97 — first, check if the drop is an unscheduled-rail item.
    const unschedRaw = e.dataTransfer.getData('application/x-unscheduled-item');
    if (unschedRaw) {
      try {
        const item = JSON.parse(unschedRaw);
        setUnschedDropTarget({
          item,
          preset: {
            employee_id: empId === 'unassigned' ? '' : empId,
            date,
            start_time: '09:00',
            end_time: '11:00',
          },
        });
      } catch { /* fall through */ }
      return;
    }
    const shift = draggingShift;
    setDraggingShift(null);
    if (!shift) return;
    moveShift(shift, empId === 'unassigned' ? '' : empId, date);
  };

  // Group shifts by employee_id and date
  const shiftsByEmpAndDate = useMemo(() => {
    const map = {};
    appointments.forEach((a) => {
      const emp = a.employee_id || 'unassigned';
      const date = a.date;
      if (!map[emp]) map[emp] = {};
      if (!map[emp][date]) map[emp][date] = [];
      map[emp][date].push(a);
    });
    return map;
  }, [appointments]);

  // iter84 — Filter visible employee rows + Unassigned row when any filter is active.
  // Previously the page kept rendering all employees with empty cells, which created
  // confusing "ghost rows" when narrowing by status/job-type/etc. Now if the dispatcher
  // is actively filtering, we only render employees who have at least one matching shift
  // (or the employee explicitly selected in filters.employee_id). With no filters set,
  // behavior is unchanged — every employee row is visible.
  const anyFilterActive = useMemo(
    () => Object.values(filters).some((v) => v !== '' && v != null),
    [filters]
  );
  const employeesById = useMemo(() => {
    const m = {};
    employees.forEach((e) => { m[e.id] = e; });
    return m;
  }, [employees]);
  // iter256 — Any assignee present on this week's appointments who ISN'T in the
  // active employee list (inactive/archived/former, or a stale id) gets a row so
  // their shifts still render. Names resolve from the full map, then the shift's
  // denormalized employee_name, then a safe fallback. Never drops a canonical shift.
  const orphanRows = useMemo(() => {
    const seen = {};
    appointments.forEach((a) => {
      // Orphan (former/inactive) lanes come ONLY from real JOB shifts. Virtual
      // holiday and time-off (PTO) blocks decorate an existing active lane — they
      // must never resurrect a former/inactive employee onto the live Schedule.
      if (a.is_holiday_block || a.is_time_off_block) return;
      const id = a.employee_id;
      if (!id || employeesById[id] || seen[id]) return;
      const from = allEmpById[id];
      seen[id] = {
        id,
        name: (from && from.name) || a.employee_name || 'Former team member',
        role: (from && from.role) || 'former',
        _inactive: true,
      };
    });
    return Object.values(seen);
  }, [appointments, employeesById, allEmpById]);
  const visibleEmployees = useMemo(() => {
    if (!anyFilterActive) return [...employees, ...orphanRows];
    if (filters.employee_id) {
      const picked = employees.filter((e) => e.id === filters.employee_id);
      const pickedOrphan = orphanRows.filter((e) => e.id === filters.employee_id);
      return [...picked, ...pickedOrphan];
    }
    const empsWithShifts = new Set(appointments.filter((a) => a.employee_id).map((a) => a.employee_id));
    return [...employees.filter((e) => empsWithShifts.has(e.id)), ...orphanRows];
  }, [employees, orphanRows, appointments, filters, anyFilterActive]);
  const showUnassignedRow = useMemo(() => {
    if (!anyFilterActive) return true;
    // Hide Unassigned row when filtering to a specific employee
    if (filters.employee_id) return false;
    return appointments.some((a) => !a.employee_id);
  }, [appointments, filters, anyFilterActive]);

  // Weekly summary
  // iter143 — Arrival windows are NOT labor hours. Surface operational counters that
  // dispatch actually needs (jobs / techs / on-job / completed / remaining / actual job
  // hours from the new actual_start_time / actual_end_time fields). The legacy "Hours"
  // number is preserved but RE-LABELLED as "Arrival Window Coverage" so it isn't mistaken
  // for real labor hours.
  const weekSummary = useMemo(() => {
    const arrivalCoverageHours = appointments.reduce((acc, a) => acc + computeHours(a.start_time, a.end_time), 0);
    const users = new Set(appointments.filter(a => a.employee_id).map(a => a.employee_id)).size;
    let onJob = 0, completedCount = 0, remaining = 0;
    let actualMinutesSum = 0;
    appointments.forEach((a) => {
      const status = (a.status || '').toLowerCase();
      const cancelled = status === 'cancelled' || status === 'canceled';
      if (cancelled) return;
      if (a.actual_end_time || status === 'completed') {
        completedCount += 1;
      } else if (a.actual_start_time || status === 'in_progress') {
        onJob += 1;
      } else {
        remaining += 1;
      }
      if (a.actual_duration_minutes != null) {
        actualMinutesSum += Number(a.actual_duration_minutes) || 0;
      }
    });
    return {
      arrivalCoverageHours,
      actualJobHours: actualMinutesSum / 60,
      shifts: appointments.length,
      users,
      onJob,
      completedCount,
      remaining,
    };
  }, [appointments]);

  const weekLabel = `${fmtETMonthDay(weekDays[0])} – ${fmtETMonthDay(weekDays[6])}`;

  return (
    <div data-testid="admin-schedule-page" className="space-y-4">
      <OnCallBanner />
      <SchedulePrintDialog
        open={printDialogOpen}
        onOpenChange={setPrintDialogOpen}
        appointments={appointments}
        employees={employees}
        weekDays={weekDays}
        weekLabel={weekLabel}
      />
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white">Schedule</h2>
          <p className="text-[#A3A3A3] text-sm">Weekly view — assign jobs to your techs</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <RefreshIndicator
            refresh={auto.refresh}
            lastUpdated={auto.lastUpdated}
            isFetching={auto.isFetching}
            isPaused={auto.isPaused}
            testId="schedule-refresh"
          />
          <div className="flex items-center gap-1 bg-[#141414] border border-[#262626] rounded-md p-1">
            <Button data-testid="schedule-prev-week" onClick={() => setWeekStart(addDays(weekStart, -7))} variant="ghost" size="sm" className="text-white hover:bg-[#262626] h-8 px-2"><ChevronLeft className="w-4 h-4" /></Button>
            <span className="px-3 text-white text-sm font-medium min-w-[150px] text-center">{weekLabel}</span>
            <Button data-testid="schedule-next-week" onClick={() => setWeekStart(addDays(weekStart, 7))} variant="ghost" size="sm" className="text-white hover:bg-[#262626] h-8 px-2"><ChevronRight className="w-4 h-4" /></Button>
          </div>
          <Button data-testid="schedule-search-btn" onClick={() => setSearchOpen(true)} variant="outline" size="sm" className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10"><Search className="w-4 h-4 mr-1" />Search</Button>
          {/* iter124 Wave M — Move-Up chip. Hidden when zero so the toolbar stays calm.
              iter125 — Purple color family for Move-Up status. */}
          {moveUpCount > 0 && (
            <Button
              data-testid="schedule-moveup-chip"
              onClick={() => setMoveUpSheetOpen(true)}
              size="sm"
              variant="outline"
              className="border-purple-500/50 text-purple-300 hover:bg-purple-500/10"
              title="Customers who want earlier service if an opening appears"
            >
              ↑ {moveUpCount} want{moveUpCount === 1 ? 's' : ''} earlier
            </Button>
          )}
          <Button data-testid="schedule-today" onClick={() => setWeekStart(getWeekStart(new Date()))} variant="outline" size="sm" className="border-[#262626] text-white hover:bg-[#262626]">Today</Button>
          <Button data-testid="schedule-print-btn" onClick={() => setPrintDialogOpen(true)} variant="outline" size="sm" className="border-[#262626] text-white hover:bg-[#262626]"><Printer className="w-4 h-4 mr-1" /> Print</Button>
          <Button
            data-testid="schedule-focus-mode"
            onClick={toggleFocusMode}
            size="sm"
            title={focusMode ? 'Exit Focus mode — restore sidebars' : 'Focus mode — collapse both sidebars for more schedule room'}
            className={focusMode
              ? 'bg-[#F3E5AB] text-[#0A0A0A] hover:bg-[#F8EDC4] border border-[#D4AF37] shadow-[0_0_0_2px_rgba(212,175,55,0.25)]'
              : 'bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold uppercase tracking-wide'
            }
          >
            {focusMode ? <Minimize2 className="w-4 h-4 mr-1.5" /> : <Maximize2 className="w-4 h-4 mr-1.5" />}
            {focusMode ? 'Exit Focus' : 'Schedule Focus'}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="schedule-add-dropdown" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Plus className="w-4 h-4 mr-1" /> Add</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#141414] border-[#262626] text-white">
              <DropdownMenuItem data-testid="add-single-shift" onClick={() => openAddShift()} className="hover:bg-[#262626] cursor-pointer focus:bg-[#262626]">Add single shift</DropdownMenuItem>
              <DropdownMenuSeparator className="bg-[#262626]" />
              <DropdownMenuItem data-testid="add-recurring-shift" onClick={() => openAddRecurring()} className="hover:bg-[#262626] cursor-pointer focus:bg-[#262626]">Add recurring shift</DropdownMenuItem>
              <DropdownMenuSeparator className="bg-[#262626]" />
              <DropdownMenuItem data-testid="add-time-off" onClick={openAddTimeOff} className="hover:bg-[#262626] cursor-pointer focus:bg-[#262626]">Add time off</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filter bar */}
      <div data-testid="schedule-filters" className="bg-[#141414] border border-[#262626] rounded-md p-3 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
        {/* iter315 — Division lives in the top "All Divisions" switcher; this is the Trade filter. */}
        <select data-testid="filter-trade" value={filters.trade_id} onChange={(e) => {
          const ok = !e.target.value || divisions.some((d) => d.trade_id === e.target.value && d.key === filters.department);
          setFilters({ ...filters, trade_id: e.target.value, department: ok ? filters.department : '' });
        }} className="bg-[#0A0A0A] border border-[#262626] rounded text-white text-xs px-2 py-1.5">
          <option value="">{activeDepartment && activeDepartment !== 'all' ? `${(departments.find((d) => d.id === activeDepartment) || {}).name || 'Trade'} (global context)` : 'All Trades'}</option>
          {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        {/* iter317 — Division filtered by the selected Trade (Plumbing Service ≠ HVAC Service). */}
        <select data-testid="filter-division" value={filters.department} onChange={(e) => setFilters({ ...filters, department: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded text-white text-xs px-2 py-1.5">
          <option value="">All Divisions</option>
          {(filters.trade_id ? divisions.filter((d) => d.trade_id === filters.trade_id) : uniqueDivisionKeys(divisions)).map((d) => <option key={d.id || d.key} value={d.key}>{d.name}</option>)}
        </select>
        <select data-testid="filter-employee" value={filters.employee_id} onChange={(e) => setFilters({...filters, employee_id: e.target.value})} className="bg-[#0A0A0A] border border-[#262626] rounded text-white text-xs px-2 py-1.5">
          <option value="">All Employees</option>
          {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <select data-testid="filter-customer" value={filters.customer_id} onChange={(e) => setFilters({...filters, customer_id: e.target.value})} className="bg-[#0A0A0A] border border-[#262626] rounded text-white text-xs px-2 py-1.5">
          <option value="">All Customers</option>
          {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select data-testid="filter-job-type" value={filters.job_type} onChange={(e) => setFilters({...filters, job_type: e.target.value})} className="bg-[#0A0A0A] border border-[#262626] rounded text-white text-xs px-2 py-1.5">
          <option value="">All Job Types</option>
          {serviceTypes.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
        </select>
        <select data-testid="filter-status" value={filters.status} onChange={(e) => setFilters({...filters, status: e.target.value})} className="bg-[#0A0A0A] border border-[#262626] rounded text-white text-xs px-2 py-1.5">
          <option value="">All Status</option>
          <option value="scheduled">Scheduled</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <input data-testid="filter-truck" value={filters.truck_id} onChange={(e) => setFilters({...filters, truck_id: e.target.value})} placeholder="Truck #" className="bg-[#0A0A0A] border border-[#262626] rounded text-white text-xs px-2 py-1.5 placeholder-[#525252]" />
        <input data-testid="filter-location" value={filters.location} onChange={(e) => setFilters({...filters, location: e.target.value})} placeholder="Location/Address" className="bg-[#0A0A0A] border border-[#262626] rounded text-white text-xs px-2 py-1.5 placeholder-[#525252]" />
      </div>

      {/* iter97 — Schedule grid + Unscheduled rail (rendered as 8th column on right). */}
      <div className="flex gap-0">
        <div className="bg-[#141414] border border-[#262626] rounded-l-md overflow-x-auto flex-1">
          <div className="min-w-[1080px]">
          {/* iter111 — Reduced min-w from 1240 → 1080 so Saturday stays visible on
              1366/1440-class screens when both sidebar AND rail are open. Each day column is
              ~129px (≈ (1080-180)/7). Horizontal scroll still kicks in on very narrow screens. */}
          {/* Day headers — iter158-cleanup: sticky so dates stay visible while scrolling down. */}
          <div className="grid grid-cols-[180px_repeat(7,minmax(0,1fr))] border-b border-[#262626] sticky top-0 z-30 bg-[#0F0F0F]">
            <div className="px-3 py-2 border-r border-[#262626] bg-[#0F0F0F]">
              <span className="text-[#A3A3A3] text-xs uppercase tracking-wider">Tech / Day</span>
            </div>
            {weekDays.map((d) => {
              const dShifts = appointments.filter(a => a.date === toISODate(d));
              return <div key={toISODate(d)} className="border-r border-[#262626] last:border-r-0"><DayHeader date={d} shifts={dShifts} capacity={capacityByDate[toISODate(d)]} dayStatusOverride={dayStatusByDate[toISODate(d)]} canEditStatus={DAY_STATUS_EDIT_ROLES.has((user?.role || '').toLowerCase())} onEditStatus={(iso) => setDayStatusEditor({ date: iso, status: dayStatusByDate[iso]?.status || '', note: dayStatusByDate[iso]?.note || '' })} /></div>;
            })}
          </div>

          {/* Unassigned row (hidden when filtering narrows away all unassigned shifts) */}
          {showUnassignedRow && (
          <div className="grid grid-cols-[180px_repeat(7,minmax(0,1fr))] border-b border-[#262626] min-h-[80px]">
            <div className="px-3 py-2 border-r border-[#262626] bg-[#0A0A0A]">
              <p className="text-[#A3A3A3] text-xs uppercase tracking-wider">Unassigned</p>
              <p className="text-white text-sm mt-1">{(shiftsByEmpAndDate.unassigned ? Object.values(shiftsByEmpAndDate.unassigned).flat().length : 0)} shifts</p>
            </div>
            {weekDays.map((d) => {
              const date = toISODate(d);
              const dShifts = shiftsByEmpAndDate.unassigned?.[date] || [];
              const cellKey = `unassigned|${date}`;
              const isHover = hoverCell === cellKey;
              return (
                <div
                  key={date}
                  data-testid={`cell-unassigned-${date}`}
                  onDoubleClick={() => openAddShift(date)}
                  onDragOver={(e) => {
                    const isUnsched = e.dataTransfer.types.includes('application/x-unscheduled-item');
                    if (draggingShift || isUnsched) {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = isUnsched ? 'copy' : 'move';
                      if (hoverCell !== cellKey) setHoverCell(cellKey);
                    }
                  }}
                  onDragLeave={() => { if (hoverCell === cellKey) setHoverCell(null); }}
                  onDrop={(e) => handleCellDrop(e, 'unassigned', date)}
                  className={`border-r border-[#262626] last:border-r-0 p-1 min-h-[80px] transition-colors ${isHover ? 'bg-[#D4AF37]/15 outline outline-2 outline-[#D4AF37]/60' : ''}`}
                >
                  {dShifts.map((s) => (
                    <ShiftCard
                      key={s.id}
                      shift={s}
                      onClick={() => openEditShift(s)}
                      onDragStart={setDraggingShift}
                      onDragEnd={() => { setDraggingShift(null); setHoverCell(null); }}
                    />
                  ))}
                </div>
              );
            })}
          </div>
          )}

          {/* Employee rows */}
          {employees.length === 0 ? (
            <div className="px-6 py-12 text-center text-[#A3A3A3]">
              No employees yet. <a href="/admin/employees" className="text-[#D4AF37] underline">Add employees</a> to assign shifts.
            </div>
          ) : visibleEmployees.length === 0 ? (
            <div data-testid="schedule-no-matching-techs" className="px-6 py-12 text-center text-[#A3A3A3]">
              No techs match the current filters.
              <button onClick={() => setFilters({ department: '', trade_id: '', employee_id: '', customer_id: '', job_type: '', status: '', truck_id: '', location: '' })} className="ml-2 text-[#D4AF37] underline hover:text-[#F3E5AB]">Clear filters</button>
            </div>
          ) : visibleEmployees.map((emp) => (
            <div key={emp.id} className="grid grid-cols-[180px_repeat(7,minmax(0,1fr))] border-b border-[#262626] last:border-b-0 min-h-[100px]">
              <div className="px-3 py-3 border-r border-[#262626] bg-[#0A0A0A] flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#D4AF37] text-[#0A0A0A] flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {emp.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">
                    {emp.name}
                    {emp._inactive && <span data-testid={`emp-former-${emp.id}`} className="ml-1.5 text-[9px] font-bold px-1 py-0.5 rounded bg-[#3f3f3f] text-[#D4D4D4] uppercase tracking-wide align-middle">Former</span>}
                  </p>
                  <p className="text-[#A3A3A3] text-[10px]">
                    {(() => {
                      const empShifts = appointments.filter(a => a.employee_id === emp.id);
                      const h = empShifts.reduce((acc, s) => acc + computeHours(s.start_time, s.end_time), 0);
                      return `${h.toFixed(1)}h • ${empShifts.length} shifts`;
                    })()}
                  </p>
                </div>
              </div>
              {weekDays.map((d) => {
                const date = toISODate(d);
                const dShifts = shiftsByEmpAndDate[emp.id]?.[date] || [];
                const cellKey = `${emp.id}|${date}`;
                const isHover = hoverCell === cellKey;
                return (
                  <div
                    key={date}
                    data-testid={`cell-${emp.id}-${date}`}
                    onDoubleClick={() => openAddShift(date, emp.id)}
                    onDragOver={(e) => {
                      const isUnsched = e.dataTransfer.types.includes('application/x-unscheduled-item');
                      if (draggingShift || isUnsched) {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = isUnsched ? 'copy' : 'move';
                        if (hoverCell !== cellKey) setHoverCell(cellKey);
                      }
                    }}
                    onDragLeave={() => { if (hoverCell === cellKey) setHoverCell(null); }}
                    onDrop={(e) => handleCellDrop(e, emp.id, date)}
                    className={`border-r border-[#262626] last:border-r-0 p-1 min-h-[100px] min-w-0 overflow-hidden hover:bg-[#1A1A1A] transition-colors cursor-pointer ${isHover ? 'bg-[#D4AF37]/15 outline outline-2 outline-[#D4AF37]/60' : ''}`}
                  >
                    {dShifts.map((s) => (
                      <ShiftCard
                        key={s.id}
                        shift={s}
                        onClick={() => openEditShift(s)}
                        onDragStart={setDraggingShift}
                        onDragEnd={() => { setDraggingShift(null); setHoverCell(null); }}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        </div>
        {/* Unscheduled Rail — sticky right column */}
        <UnscheduledRail
          initialFilter={searchParams.get('rail') || undefined}
          employees={employees}
          onScheduled={() => fetchAppointments()}
          collapsed={railCollapsed}
          onToggleCollapsed={(v) => setRailCollapsed(typeof v === 'boolean' ? v : !railCollapsed)}
          // iter188 — Route "+ Unscheduled Job" and per-draft "Edit" to the shared New Shift modal.
          onAddUnscheduled={() => openAddUnscheduled()}
          onEditUnscheduled={(item) => openEditUnscheduled(item)}
          // iter316 — Needs Scheduling — Return Visit → existing Add Visit to Existing Job flow.
          onAddReturnVisit={(item) => setAddVisit({ open: true, date: (item.promised || item.suggested || {}).date || '', preset: { jobId: item.job_id, returnAction: item } })}
          onOpenSource={(item) => {
            if (item.source === 'pm_sr') navigate('/admin/pm/service-requests');
            else if (item.source === 'customer_sr') navigate('/admin/service-requests');
          }}
          // iter128 — Accept scheduled shifts dropped onto the rail (move back to Unscheduled).
          // The rail captures shift_id via dataTransfer text/plain and asks for confirmation.
          onShiftDroppedToUnscheduled={async (shiftId) => {
            const shift = appointments.find((a) => a.id === shiftId);
            if (!shift) return;
            const fromDate = shift.date;
            const fromTime = `${shift.start_time}–${shift.end_time}`;
            const ok = window.confirm(
              `Move this scheduled job back to Unscheduled?\n\n${shift.shift_title || shift.service_type || 'Job'} · ${shift.customer_name || 'No customer'}\nCurrently: ${fromDate} ${fromTime}\n\nAll job details, customer info, photos, notes, and helpers will be preserved.`
            );
            if (!ok) return;
            try {
              await axios.post(`${API}/admin/appointments/${shiftId}/move-to-unscheduled`, {}, { withCredentials: true });
              toast.success('Moved to Unscheduled');
              await fetchAppointments();
              if (typeof window.__nexflow_unscheduled_reload === 'function') {
                await window.__nexflow_unscheduled_reload();
              }
            } catch (e) {
              toast.error(e.response?.data?.detail || 'Failed to move to Unscheduled');
            }
          }}
        />
      </div>

      {/* iter143 — Day/Week summary footer: dispatcher day-health counters.
          Arrival window coverage is preserved but clearly LABELLED so it isn't
          mistaken for real labor hours. Actual job hours come from actual_start /
          actual_end time stamps. */}
      <div data-testid="schedule-day-health" className="bg-[#141414] border border-[#262626] rounded-md p-4 flex items-center justify-between flex-wrap gap-3">
        <p className="text-[#A3A3A3] text-xs uppercase tracking-wider font-medium">Day / Week Health</p>
        <div className="flex items-center gap-4 text-sm flex-wrap">
          <div className="flex items-center gap-2" data-testid="dh-shifts"><Briefcase className="w-4 h-4 text-[#D4AF37]" /><span className="text-[#A3A3A3]">Jobs</span><span className="text-white font-bold">{weekSummary.shifts}</span></div>
          <div className="flex items-center gap-2" data-testid="dh-techs"><Users className="w-4 h-4 text-[#D4AF37]" /><span className="text-[#A3A3A3]">Techs</span><span className="text-white font-bold">{weekSummary.users}</span></div>
          <div className="flex items-center gap-2" data-testid="dh-on-job"><span className="w-2 h-2 rounded-full bg-blue-400"></span><span className="text-[#A3A3A3]">On Job</span><span className="text-blue-300 font-bold">{weekSummary.onJob}</span></div>
          <div className="flex items-center gap-2" data-testid="dh-completed"><span className="w-2 h-2 rounded-full bg-green-400"></span><span className="text-[#A3A3A3]">Completed</span><span className="text-green-300 font-bold">{weekSummary.completedCount}</span></div>
          <div className="flex items-center gap-2" data-testid="dh-remaining"><span className="w-2 h-2 rounded-full bg-orange-400"></span><span className="text-[#A3A3A3]">Remaining</span><span className="text-orange-300 font-bold">{weekSummary.remaining}</span></div>
          {/* iter167 — Billing Missing safety chip. Only renders when there's at
              least one completed customer-facing shift that has no documented
              revenue/closeout outcome. Clicking it opens a drill-down list. */}
          {billingMissing.count > 0 && (
            <button
              data-testid="dh-billing-missing"
              onClick={() => setBillingMissingOpen(true)}
              title="Completed customer jobs with no invoice, draft, warranty, $0 membership, or no-billing reason. Click to review."
              className="flex items-center gap-2 px-2 py-0.5 rounded bg-red-900/30 border border-red-800/60 hover:bg-red-900/50 transition-colors"
            >
              <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse"></span>
              <span className="text-red-200 text-xs">Billing Missing</span>
              <span className="text-red-300 font-bold">{billingMissing.count}</span>
            </button>
          )}
          <div className="flex items-center gap-2" data-testid="dh-actual-hours"><Clock className="w-4 h-4 text-[#D4AF37]" /><span className="text-[#A3A3A3]">Actual Job h</span><span className="text-white font-bold">{weekSummary.actualJobHours.toFixed(1)}</span></div>
          <div className="flex items-center gap-2 opacity-70" data-testid="dh-arrival-coverage" title="This is the sum of customer arrival windows (e.g. 8 AM – 12 PM). It is NOT labor hours.">
            <Clock className="w-4 h-4 text-[#525252]" />
            <span className="text-[#525252]">Arrival Window Coverage</span>
            <span className="text-[#A3A3A3] font-bold">{weekSummary.arrivalCoverageHours.toFixed(1)}h</span>
          </div>
        </div>
        <p className="text-[#A3A3A3] text-xs">Actual job hours come from Start Job / Complete tap-times — not arrival windows.</p>
      </div>

      {/* iter221 — Recurring Shift builder (Phase B) */}
      <Sheet open={recurringOpen} onOpenChange={setRecurringOpen}>
        <SheetContent className="bg-[#141414] border-l border-[#262626] text-white w-full sm:max-w-md overflow-y-auto" data-testid="recurring-modal">
          <SheetHeader>
            <SheetTitle className="text-white">Add Recurring Shift</SheetTitle>
            <SheetDescription className="text-[#A3A3A3]">Creates one series that generates a separate shift for every selected date.</SheetDescription>
          </SheetHeader>
          <div className="space-y-3 mt-4">
            <div>
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Shift title</Label>
              <Input data-testid="rec-title" value={recForm.shift_title} onChange={(e) => setRecForm({ ...recForm, shift_title: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" placeholder="Shop Cleanup / Restock" />
            </div>
            <div>
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Service type / activity</Label>
              <select data-testid="rec-service-type" value={recForm.service_type} onChange={(e) => setRecForm({ ...recForm, service_type: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-2 text-white text-sm">
                <option value="">Select…</option>
                {serviceTypes.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-white">
              <input type="checkbox" data-testid="rec-internal" checked={recForm.is_internal_activity} onChange={(e) => setRecForm({ ...recForm, is_internal_activity: e.target.checked })} className="accent-[#D4AF37]" />
              Internal activity (no invoice/estimate, never billing-missing)
            </label>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs text-[#A3A3A3]">Start time</Label><Input data-testid="rec-start-time" type="time" value={recForm.start_time} onChange={(e) => setRecForm({ ...recForm, start_time: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
              <div><Label className="text-xs text-[#A3A3A3]">End time</Label><Input data-testid="rec-end-time" type="time" value={recForm.end_time} onChange={(e) => setRecForm({ ...recForm, end_time: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
            </div>
            <div>
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Repeat</Label>
              <select data-testid="rec-repeat" value={recForm.repeat} onChange={(e) => setRecForm({ ...recForm, repeat: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-2 text-white text-sm">
                <option value="daily">Daily</option><option value="weekly">Weekly</option><option value="biweekly">Every other week</option>
              </select>
            </div>
            {recForm.repeat !== 'daily' && (
              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Days</Label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((d) => (
                    <button key={d} type="button" data-testid={`rec-weekday-${d}`}
                      onClick={() => setRecForm({ ...recForm, weekdays: recForm.weekdays.includes(d) ? recForm.weekdays.filter((x) => x !== d) : [...recForm.weekdays, d] })}
                      className={`text-[11px] px-2 h-7 rounded border ${recForm.weekdays.includes(d) ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#262626] text-[#A3A3A3]'}`}>{d.slice(0, 3)}</button>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs text-[#A3A3A3]">Start date</Label><Input data-testid="rec-start-date" type="date" value={recForm.start_date} onChange={(e) => setRecForm({ ...recForm, start_date: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
              <div>
                <Label className="text-xs text-[#A3A3A3]">Ends</Label>
                <select data-testid="rec-end-mode" value={recForm.end_mode} onChange={(e) => setRecForm({ ...recForm, end_mode: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-2 text-white text-sm">
                  <option value="date">On date</option><option value="count"># occurrences</option>
                </select>
              </div>
            </div>
            {recForm.end_mode === 'date'
              ? <div><Label className="text-xs text-[#A3A3A3]">End date</Label><Input data-testid="rec-end-date" type="date" value={recForm.end_date} onChange={(e) => setRecForm({ ...recForm, end_date: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
              : <div><Label className="text-xs text-[#A3A3A3]">Occurrences</Label><Input data-testid="rec-occurrences" type="number" value={recForm.occurrences} onChange={(e) => setRecForm({ ...recForm, occurrences: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>}
            <div>
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Primary / Lead</Label>
              <select data-testid="rec-primary" value={recForm.employee_id} onChange={(e) => setRecForm({ ...recForm, employee_id: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-2 text-white text-sm">
                <option value="">Select primary…</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Assigned Crew</Label>
              <select data-testid="rec-crew-add" value="" onChange={(e) => {
                const v = e.target.value; if (!v || v === recForm.employee_id || (recForm.crew || []).some((c) => c.employee_id === v)) return;
                const emp = employees.find((x) => x.id === v); if (!emp) return;
                setRecForm({ ...recForm, crew: [...(recForm.crew || []), { employee_id: v, name: emp.name, role: 'assigned_technician' }] });
              }} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-2 text-white text-sm">
                <option value="">Add crew member…</option>
                {employees.filter((e) => e.id !== recForm.employee_id && !(recForm.crew || []).some((c) => c.employee_id === e.id)).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
              {(recForm.crew || []).map((c) => (
                <div key={c.employee_id} className="flex items-center gap-2 mt-1 bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1.5">
                  <span className="text-white text-xs flex-1">{c.name}</span>
                  <select value={c.role} onChange={(e) => setRecForm({ ...recForm, crew: recForm.crew.map((x) => x.employee_id === c.employee_id ? { ...x, role: e.target.value } : x) })} className="bg-[#141414] border border-[#262626] rounded text-white text-[11px] px-1.5 py-1">
                    <option value="assigned_technician">Assigned Technician</option><option value="helper">Helper / Apprentice</option><option value="office_support">Office / Support</option><option value="other">Other</option>
                  </select>
                  <button type="button" onClick={() => setRecForm({ ...recForm, crew: recForm.crew.filter((x) => x.employee_id !== c.employee_id) })} className="text-[#A3A3A3] hover:text-red-400">×</button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 pt-2">
              <Button data-testid="rec-submit" onClick={submitRecurring} disabled={recSaving} className="flex-1 bg-[#D4AF37] text-black hover:bg-[#F3E5AB]">{recSaving ? 'Creating…' : 'Create recurring series'}</Button>
              <Button variant="outline" onClick={() => setRecurringOpen(false)} className="border-[#262626] text-[#A3A3A3]">Cancel</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Master-Schedule "Add Time Off" — canonical PTO/Unpaid flow (no customer/invoice) */}
      <Sheet open={timeOffOpen} onOpenChange={setTimeOffOpen}>
        <SheetContent className="bg-[#141414] border-l border-[#262626] text-white w-full sm:max-w-md overflow-y-auto" data-testid="timeoff-modal">
          <SheetHeader>
            <SheetTitle className="text-white">Add Time Off</SheetTitle>
            <SheetDescription className="text-[#A3A3A3]">
              Blocks the employee's availability. No customer, invoice or Billing-Missing is created. Paid PTO draws the PTO bank; Unpaid books 0 paid hours.
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Employee</Label>
              <select
                data-testid="timeoff-employee"
                value={timeOffForm.employee_id}
                onChange={(e) => setTimeOffForm({ ...timeOffForm, employee_id: e.target.value })}
                className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-2 text-white text-sm"
              >
                <option value="">Select employee…</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Type</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  data-testid="timeoff-type-pto"
                  onClick={() => setTimeOffForm({ ...timeOffForm, time_off_type: 'pto' })}
                  className={`h-10 rounded border text-sm font-medium ${timeOffForm.time_off_type === 'pto' ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#262626] text-[#A3A3A3] hover:text-white'}`}
                >
                  Paid PTO
                </button>
                <button
                  type="button"
                  data-testid="timeoff-type-unpaid"
                  onClick={() => setTimeOffForm({ ...timeOffForm, time_off_type: 'unpaid' })}
                  className={`h-10 rounded border text-sm font-medium ${timeOffForm.time_off_type === 'unpaid' ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#262626] text-[#A3A3A3] hover:text-white'}`}
                >
                  Unpaid Time Off
                </button>
              </div>
              <p className="text-[10px] text-[#525252]">
                {timeOffForm.time_off_type === 'unpaid'
                  ? 'Unpaid — consumes 0 PTO and books 0 paid hours. Still blocks the schedule.'
                  : 'Paid PTO — draws the PTO bank and books paid hours per company policy.'}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Start date</Label>
                <Input data-testid="timeoff-start-date" type="date" value={timeOffForm.start_date} onChange={(e) => setTimeOffForm({ ...timeOffForm, start_date: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">End date</Label>
                <Input data-testid="timeoff-end-date" type="date" value={timeOffForm.end_date} min={timeOffForm.start_date} onChange={(e) => setTimeOffForm({ ...timeOffForm, end_date: e.target.value })} placeholder="Same as start" className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
            </div>
            <p className="text-[10px] text-[#525252] -mt-2">Leave end date blank for a single day.</p>
            <div className="space-y-1.5">
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Reason / notes (optional)</Label>
              <Textarea data-testid="timeoff-reason" value={timeOffForm.reason} onChange={(e) => setTimeOffForm({ ...timeOffForm, reason: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" rows={2} placeholder="e.g. Vacation, family day…" />
            </div>
            <div className="flex gap-2 pt-1">
              <Button data-testid="timeoff-submit" onClick={() => submitTimeOff(false)} disabled={timeOffSaving} className="flex-1 bg-[#D4AF37] text-black hover:bg-[#F3E5AB]">
                {timeOffSaving ? 'Scheduling…' : 'Schedule Time Off'}
              </Button>
              <Button variant="outline" onClick={() => setTimeOffOpen(false)} className="border-[#262626] text-[#A3A3A3]">Cancel</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AddVisitSheet
        open={addVisit.open}
        onOpenChange={(o) => setAddVisit((p) => ({ ...p, open: o }))}
        employees={employees}
        defaultDate={addVisit.date}
        preset={addVisit.preset}
        onDone={() => { fetchAppointments(); if (window.__nexflow_unscheduled_reload) window.__nexflow_unscheduled_reload(); }}
      />

      {/* Shift Sheet */}
      <Sheet open={modalOpen} onOpenChange={setModalOpen}>
        <SheetContent className="bg-[#141414] border-l border-[#262626] text-white w-full sm:max-w-md overflow-y-auto" data-testid="shift-modal">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center justify-between">
              <span data-testid="shift-modal-title">
                {unscheduledMode
                  ? (unscheduledDraftId ? 'Edit Unscheduled Job' : 'New Unscheduled Job')
                  : (editingShift ? 'Edit Shift' : 'New Shift')}
              </span>
              {unscheduledMode && (
                <span
                  data-testid="unscheduled-mode-badge"
                  className="text-[10px] font-bold uppercase tracking-wider text-amber-200 bg-amber-500/15 border border-amber-500/40 px-2 py-0.5 rounded"
                >
                  Holding Area
                </span>
              )}
            </SheetTitle>
            <SheetDescription className="text-[#A3A3A3]">
              {unscheduledMode
                ? 'Same form as a regular shift — but date, time, and tech are all optional. Fill in what you know now, save to the Unscheduled rail, and finish scheduling later. Once Trade, Division, Service Type, date and time are filled, click "Schedule Now" (leave tech blank for Unassigned).'
                : 'Fill in the shift details below. Unassigned shifts are visible to eligible techs by default; check "Lock from tech claiming" only for rare exceptions.'}
            </SheetDescription>
          </SheetHeader>
          {unscheduledMode && (
            <div data-testid="unsched-required-status" className={`mt-3 rounded-md border px-3 py-2 text-xs ${unschedMissing.length ? 'border-amber-500/40 bg-amber-500/10 text-amber-200' : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'}`}>
              {form.job_number && <div className="font-bold mb-0.5" data-testid="unsched-job-number">Stays on Job {form.job_number}</div>}
              {unschedMissing.length
                ? <>Missing to schedule: <span className="font-bold" data-testid="unsched-missing-list">{unschedMissing.join(', ')}</span></>
                : 'Ready to schedule.'}
              {form.date && form.date < todayISO_ET() && (
                <div data-testid="unsched-past-date-warning" className="mt-1 text-amber-300">Heads up: {form.date} is in the past.</div>
              )}
            </div>
          )}
          {!editingShift && !unscheduledMode && (
            <div className="mt-3 grid grid-cols-2 gap-1 p-1 rounded-md bg-[#0A0A0A] border border-[#262626]" data-testid="shift-mode-toggle">
              <button data-testid="shift-mode-new-job" className="text-xs font-bold py-1.5 rounded bg-[#D4AF37] text-[#0A0A0A]">Create New Job</button>
              <button data-testid="shift-mode-add-visit" onClick={() => { setModalOpen(false); setAddVisit({ open: true, preset: null, date: form.date }); }} className="text-xs font-bold py-1.5 rounded text-[#A3A3A3] hover:text-white">Add Visit to Existing Job</button>
            </div>
          )}

          {/* iter116 — Admin Correction Tool. Visible only when the shift already has
              a completion (tech submitted) AND viewer is super_admin/admin/service_manager.
              iter169 — When the completion has no invoice_status (partial closeout),
              the banner flips to a red "Finish Billing" CTA so dispatch can resume
              the closeout directly from the Edit Shift sheet. */}
          {editingShift && editingShift.completion_id && canCorrectCompletion(user) && (
            editingShift.needs_invoice_decision ? (
              <div
                data-testid="edit-shift-finish-billing-banner"
                className="mt-4 -mx-1 px-3 py-2 rounded-md bg-red-900/25 border border-red-500/60 flex items-center justify-between gap-2"
              >
                <div className="min-w-0">
                  <p className="text-red-200 text-xs font-bold uppercase tracking-wider">Billing Not Finished</p>
                  <p className="text-[#FCA5A5] text-[11px]">Tech submitted closeout but never picked an invoice decision. Pick the outcome to clear it from Billing Missing.</p>
                </div>
                <Button
                  data-testid="open-finish-billing"
                  type="button"
                  onClick={() => setEditingCompletionId(editingShift.completion_id)}
                  className="bg-red-600 text-white hover:bg-red-500 font-bold whitespace-nowrap"
                >
                  Finish Billing
                </Button>
              </div>
            ) : (
              <div className="mt-4 -mx-1 px-3 py-2 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[#D4AF37] text-xs font-bold uppercase tracking-wider">Closeout Submitted</p>
                  <p className="text-[#A3A3A3] text-[11px]">Fix wrong payment method, checklist answers, review opt-in, etc.</p>
                </div>
                <Button
                  data-testid="open-correct-completion"
                  type="button"
                  onClick={() => setEditingCompletionId(editingShift.completion_id)}
                  className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold whitespace-nowrap"
                >
                  Correct Completion
                </Button>
              </div>
            )
          )}

          {/* iter195 — Field Completion Details, read-only. Visible to ANY
              internal viewer (not gated behind Correction permission) so office
              staff can see every field the tech entered without needing to open
              the correction sheet. Renders above the invoice list so recommended
              follow-ups, tech notes, payment notes, etc. are impossible to miss. */}
          {editingShift && editingShift.completion_id && (
            <div className="mt-3 -mx-1 px-1">
              <FieldCompletionDetails completionId={editingShift.completion_id} />
            </div>
          )}

          {/* 911-fix-iter172 (Issue #2) — Shift-linked invoices. Lets dispatch
              View/Edit submitted invoices straight from the Edit Shift sheet
              instead of bouncing out to /admin/invoices. Renders one row per
              invoice in the group (HSP claims produce a customer-OOP row +
              a warranty-draft row). */}
          {editingShift && editingShiftInvoices.length > 0 && (
            <div data-testid="edit-shift-shift-invoices" className="mt-3 -mx-1 px-3 py-2 rounded-md bg-[#141414] border border-[#262626]">
              <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold mb-2">
                {editingShiftInvoices.length === 1 ? 'Invoice for this shift' : `Invoice Group (${editingShiftInvoices.length})`}
              </p>
              <div className="space-y-1.5">
                {editingShiftInvoices.map((inv) => {
                  const payor = inv.payor_kind === 'warranty' ? 'Warranty' : (inv.covered_by_membership ? 'Membership' : 'Customer');
                  const amt = typeof inv.amount === 'number' ? `$${inv.amount.toFixed(2)}` : '';
                  return (
                    <div
                      key={inv.id}
                      data-testid={`edit-shift-invoice-${inv.id}`}
                      className="bg-[#0A0A0A] border border-[#262626] rounded p-2 flex items-center justify-between gap-2"
                    >
                      <div className="min-w-0">
                        <p className="text-white text-sm font-bold truncate">
                          {inv.invoice_number || `Invoice ${inv.id.slice(-6)}`}{' '}
                          <span className="text-[#A3A3A3] font-normal">· {amt} · {payor}</span>
                        </p>
                        <p className="text-[#737373] text-[11px] uppercase tracking-wider">
                          {(inv.status || 'pending').replace(/_/g, ' ')}
                          {inv.office_billing_status && inv.office_billing_status !== 'needs_review' && (
                            <span className="ml-1.5 text-[#D4AF37]">· QB: {inv.office_billing_status.replace(/_/g, ' ')}</span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          data-testid={`edit-shift-invoice-view-${inv.id}`}
                          onClick={() => navigate(`/admin/invoices?status=all&open=${inv.id}`)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#262626] hover:bg-[#3a3a3a] text-white text-xs font-bold"
                        >
                          View
                        </button>
                        <button
                          type="button"
                          data-testid={`edit-shift-invoice-edit-${inv.id}`}
                          onClick={() => navigate(`/admin/invoices?status=all&open=${inv.id}&edit=1`)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] text-xs font-bold"
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-[#737373] text-[11px] italic mt-2">
                This shift already has an invoice on file. Editing it updates the existing record — no duplicate created.
              </p>
            </div>
          )}

          {/* iter144 — Dispatcher Actual-Time controls. Lets admins / dispatchers stamp
              actual_start_time and actual_end_time from the Edit Shift sheet so they
              don't have to ask the tech to tap their device. Idempotent on backend —
              re-clicks return the existing timestamp without overwriting. The original
              customer arrival window above stays untouched (Date + Start/End fields). */}
          {editingShift && !['completed', 'cancelled', 'canceled'].includes((editingShift.status || '').toLowerCase()) && (
            <div data-testid="shift-actual-time-controls" className="mt-4 -mx-1 px-3 py-3 rounded-md bg-[#0A0A0A] border border-[#262626] space-y-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3] font-bold">Actual Job Time (Labor)</p>
                  <p className="text-[10px] text-[#525252]">Stamps the tech&apos;s actual on-site work. Does NOT modify the customer arrival window.</p>
                </div>
                {editingShift.actual_start_time && (
                  <span data-testid="shift-on-job-indicator" className="text-[10px] font-bold uppercase tracking-wider text-blue-300 border border-blue-500/40 bg-blue-500/10 px-2 py-0.5 rounded">
                    On Job {fmtT(editingShift.actual_start_time)}
                  </span>
                )}
                {editingShift.actual_end_time && (
                  <span data-testid="shift-done-indicator" className="text-[10px] font-bold uppercase tracking-wider text-green-300 border border-green-500/40 bg-green-500/10 px-2 py-0.5 rounded">
                    Done {fmtT(editingShift.actual_end_time)}
                    {editingShift.actual_duration_minutes != null ? ` · ${fmtDur(editingShift.actual_duration_minutes)}` : ''}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  data-testid="shift-start-job"
                  type="button"
                  size="sm"
                  disabled={!!editingShift.actual_start_time}
                  onClick={async () => {
                    try {
                      const { data } = await axios.post(`${API}/schedule/${editingShift.id}/start-job`, {}, { withCredentials: true });
                      // iter144 — Optimistic refresh of the modal state plus a refetch
                      // so the schedule grid status tag flips immediately.
                      const updated = { ...editingShift, actual_start_time: data.actual_start_time, status: 'in_progress' };
                      setEditingShift(updated);
                      await fetchAppointments();
                      toast.success(editingShift.actual_start_time ? 'Already started' : 'Job started');
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Could not start job');
                    }
                  }}
                  className="bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-40 disabled:hover:bg-blue-600"
                >
                  <Play className="w-3.5 h-3.5 mr-1.5" />
                  {editingShift.actual_start_time ? 'Started' : 'Start Job'}
                </Button>
                <Button
                  data-testid="shift-mark-end-time"
                  type="button"
                  size="sm"
                  disabled={!!editingShift.actual_end_time}
                  onClick={async () => {
                    try {
                      const { data } = await axios.post(`${API}/schedule/${editingShift.id}/complete-job-time`, {}, { withCredentials: true });
                      const updated = {
                        ...editingShift,
                        actual_end_time: data.actual_end_time || editingShift.actual_end_time,
                        actual_start_time: editingShift.actual_start_time || data.actual_start_time,
                        actual_duration_minutes: data.actual_duration_minutes ?? editingShift.actual_duration_minutes,
                      };
                      setEditingShift(updated);
                      await fetchAppointments();
                      toast.success(editingShift.actual_end_time ? 'Already stamped' : 'End time stamped');
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Could not stamp end time');
                    }
                  }}
                  variant="outline"
                  className="border-green-700/50 text-green-300 hover:bg-green-700/10 disabled:opacity-40"
                  title="Stamp actual end time without running the full Completion Wizard"
                >
                  <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
                  {editingShift.actual_end_time ? 'Ended' : 'Mark End Time'}
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-4 mt-6">
            {/* Date + All day */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Date</Label>
                <Input data-testid="shift-date" type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
              <div className="flex items-end gap-2 pb-1">
                <Switch data-testid="shift-all-day" checked={form.all_day} onCheckedChange={(v) => setForm({...form, all_day: v})} className="data-[state=checked]:bg-[#D4AF37]" />
                <Label className="text-white">All day</Label>
              </div>
            </div>

            {!form.all_day && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Start</Label>
                  <Input data-testid="shift-start" type="time" value={form.start_time} onChange={(e) => setForm({...form, start_time: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">End</Label>
                  <Input data-testid="shift-end" type="time" value={form.end_time} onChange={(e) => setForm({...form, end_time: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white" />
                </div>
              </div>
            )}

            {!form.all_day && (
              <p className="text-[#D4AF37] text-sm font-bold">{computeHours(form.start_time, form.end_time).toFixed(2)} Hours</p>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Shift Title</Label>
              <Input data-testid="shift-title-input" value={form.shift_title} onChange={(e) => setForm({...form, shift_title: e.target.value})} placeholder="Type here" className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>

            {/* Job Template selector */}
            {jobTemplates.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Job Template (optional)</Label>
                <Select value={form.template_id || 'none'} onValueChange={(v) => {
                  if (v === 'none') { setForm((p) => ({ ...p, template_id: '' })); return; }
                  const tmpl = jobTemplates.find((t) => t.id === v);
                  applyTemplate(tmpl);
                }}>
                  <SelectTrigger data-testid="shift-template" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue placeholder="Apply a template…" /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">
                    <SelectItem value="none" className="text-white italic">— No template —</SelectItem>
                    {jobTemplates.map((t) => (
                      <SelectItem key={t.id} value={t.id} className="text-white" data-testid={`shift-template-opt-${t.id}`}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Trade {!editingShift && <span className="text-red-400">*</span>}</Label>
              <Select value={form.department_id || ''} onValueChange={(v) => {
                // iter317 — Trade change clears an incompatible Division + Service Type.
                const div = divisions.find((d) => d.trade_id === v && d.key === form.department && d.active);
                const cur = serviceTypeMap[form.service_type];
                const keepSt = cur && (!cur.department_id || cur.department_id === v) && (!cur.division_id || cur.division_id === div?.id);
                setForm({ ...form, department_id: v, department: div ? form.department : '', service_type: keepSt ? form.service_type : '' });
              }}>
                <SelectTrigger data-testid="shift-department" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue placeholder="Select trade…" /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  {departments.map((d) => (
                    <SelectItem key={d.id} value={d.id} className="text-white" data-testid={`shift-department-opt-${d.id}`}>
                      {d.name}{d.type === 'internal' ? ' (internal)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Division {!editingShift && <span className="text-red-400">*</span>}</Label>
              <Select value={form.department || ''} disabled={!form.department_id && !editingShift} onValueChange={(v) => {
                // iter317 — Division change clears a Service Type mapped to another Division.
                const div = divisions.find((d) => d.trade_id === form.department_id && d.key === v);
                const cur = serviceTypeMap[form.service_type];
                const keepSt = cur && (!cur.division_id || cur.division_id === div?.id);
                setForm({ ...form, department: v, service_type: keepSt ? form.service_type : '' });
              }}>
                <SelectTrigger data-testid="shift-division" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue placeholder={form.department_id ? 'Select division…' : 'Pick a Trade first'} /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  {shiftDivisionOpts.map((d) => (
                    <SelectItem key={d.id || d.key} value={d.key} className="text-white" data-testid={`shift-division-opt-${d.key}`}>{d.name}</SelectItem>
                  ))}
                  {form.department_id && shiftDivisionOpts.length === 0 && <div className="px-2 py-1 text-[#A3A3A3] text-xs">No divisions set up for this Trade</div>}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Service Type</Label>
              <div className="flex gap-2">
                <Select value={(!unscheduledMode || shiftServiceTypeOpts.some((s) => s.name === form.service_type)) ? (form.service_type || '') : ''} disabled={!(form.department_id && form.department) && !editingShift} onValueChange={(v) => setForm({...form, service_type: v, color: COLOR_FOR_NAME(v)})}>
                  <SelectTrigger data-testid="shift-job" className="bg-[#0A0A0A] border-[#262626] text-white flex-1"><SelectValue placeholder={form.department_id && form.department ? (shiftServiceTypeOpts.length ? 'Select service type…' : 'No mapped Service Types') : 'Pick Trade + Division first'} /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626] max-h-72 overflow-y-auto">
                    {serviceTypes.length === 0 && <div className="px-2 py-1 text-[#A3A3A3] text-xs">Loading…</div>}
                    {serviceTypes.length > 0 && form.department_id && form.department && shiftServiceTypeOpts.length === 0 && (
                      <div data-testid="shift-job-empty" className="px-2 py-2 text-xs text-orange-300">No active Service Types are mapped to this Trade + Division yet. Map one on the Service Types page.</div>
                    )}
                    {shiftServiceTypeOpts.map((s, i, arr) => (
                      <React.Fragment key={s.id}>
                      {shiftDivisionId && !s.division_id && (i === 0 || arr[i - 1].division_id) && (
                        <div data-testid="shift-job-unassigned-header" className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-wider text-orange-300 border-t border-[#262626]">Not yet mapped — assign these on Service Types</div>
                      )}
                      <SelectItem value={s.name} className="text-white" data-testid={`shift-job-opt-${s.id}`}>
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLOR_FOR_NAME(s.name) }} />
                          <span>{s.name}</span>
                          {!s.customer_required && <span className="ml-1.5 text-[10px] text-[#A3A3A3]">(internal)</span>}
                        </div>
                      </SelectItem>
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
                <div className="w-10 h-10 rounded-md border border-[#262626] flex-shrink-0" style={{ backgroundColor: form.color }} />
              </div>
              {form.service_type && !currentRequiresCustomer && (
                <p className="text-[10px] text-[#A3A3A3]">Internal type — customer not required.</p>
              )}
              {unscheduledMode && serviceTypes.length > 0 && form.department_id && form.department && shiftServiceTypeOpts.length === 0 && (
                <p data-testid="shift-st-none-mapped" className="text-[10px] text-orange-300">No active Service Types are mapped to this Trade + Division yet — map one on the Service Types page, then come back to schedule.</p>
              )}
              {unscheduledMode && form.service_type && form.department_id && form.department && !mappedServiceType && (
                <p data-testid="shift-st-unmapped-required" className="text-[10px] text-orange-300">"{form.service_type}" is not mapped to this Trade + Division yet — pick a mapped Service Type to schedule. The global Service Type record is not changed.</p>
              )}
              {unscheduledMode && unschedOriginal && (unschedOriginal.service_type || unschedOriginal.department) && (
                <p data-testid="shift-unsched-original" className="text-[10px] text-[#A3A3A3]">Original record: {unschedOriginal.service_type || '—'}{unschedOriginal.department ? ` · ${unschedOriginal.department}` : ''}{!unschedOriginal.department_id ? ' · no Trade' : ''}</p>
              )}
              {!unscheduledMode && form.service_type && currentServiceType && !currentServiceType.division_id && (
                <p data-testid="shift-st-unassigned-warning" className="text-[10px] text-orange-300">This service type is not mapped to a Trade + Division yet — assign it on the Service Types page.</p>
              )}
              {editingShift && <OriginalRecordNote visit={editingShift} />}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Assigned User</Label>
              <Select value={form.employee_id || 'unassigned'} onValueChange={(v) => setForm({...form, employee_id: v === 'unassigned' ? '' : v, claimable: v === 'unassigned' ? !form.claim_locked : false})}>
                <SelectTrigger data-testid="shift-employee" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue placeholder="Select user" /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  <SelectItem value="unassigned" className="text-white italic">— Unassigned —</SelectItem>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id} className="text-white">{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {/* iter132 — Visibility & claiming model change:
                  Unassigned jobs are AUTOMATICALLY claimable (no checkbox required).
                  This control is the rare exception — "Lock from tech claiming" — for
                  dispatch-only jobs that should NOT appear in the Available Jobs rail.
                  Only shown for unassigned shifts; assigned jobs are never claim-able. */}
              {!form.employee_id && (
                <label
                  data-testid="shift-claim-lock-row"
                  className="flex items-start gap-2 mt-2 cursor-pointer select-none rounded-md border border-[#262626] bg-[#0A0A0A] px-3 py-2"
                >
                  <input
                    type="checkbox"
                    data-testid="shift-claim-lock-toggle"
                    checked={!!form.claim_locked}
                    onChange={(e) => setForm({ ...form, claim_locked: e.target.checked })}
                    className="mt-0.5 accent-[#D4AF37] w-4 h-4"
                  />
                  <span className="text-xs">
                    <span className="text-white font-bold">Lock from tech claiming</span>
                    <span className="text-[#A3A3A3] block leading-snug">
                      By default, unassigned jobs are visible & claimable by eligible techs.
                      Check this only if you need to hide this specific job from the claim queue.
                    </span>
                  </span>
                </label>
              )}
            </div>

            {/* iter220 — Assigned Crew (Phase A). Multiple employees with roles.
                The Primary/Lead (employee_id above) owns closeout; everyone here is
                a full crew member (not just a helper). */}
            <div className="space-y-1.5">
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Assigned Crew</Label>
              <p className="text-[10px] text-[#525252]">The assigned employee above is the Primary/Lead. Add other crew members and set each role.</p>
              <Select value="__add__" onValueChange={(v) => {
                if (v === '__add__' || !v) return;
                if ((form.crew || []).some((c) => c.employee_id === v) || v === form.employee_id) return;
                const emp = employees.find((e) => e.id === v);
                if (!emp) return;
                setForm({ ...form, crew: [...(form.crew || []), { employee_id: v, name: emp.name, role: 'assigned_technician' }] });
              }}>
                <SelectTrigger data-testid="shift-crew-add" className="bg-[#0A0A0A] border-[#262626] text-white">
                  <SelectValue placeholder={(form.crew || []).length ? `${(form.crew || []).length} crew member(s) — add another` : 'Add crew member'} />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  {employees
                    .filter((e) => e.id !== form.employee_id && !(form.crew || []).some((c) => c.employee_id === e.id))
                    .map((e) => (
                      <SelectItem key={e.id} value={e.id} className="text-white">{e.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {(form.crew || []).length > 0 && (
                <div className="space-y-1.5 mt-1" data-testid="shift-crew-list">
                  {(form.crew || []).map((c) => (
                    <div key={c.employee_id} data-testid={`shift-crew-row-${c.employee_id}`} className="flex items-center gap-2 bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1.5">
                      <span className="text-white text-xs flex-1">{c.name}</span>
                      <select
                        data-testid={`shift-crew-role-${c.employee_id}`}
                        value={c.role}
                        onChange={(e) => setForm({ ...form, crew: (form.crew || []).map((x) => x.employee_id === c.employee_id ? { ...x, role: e.target.value } : x) })}
                        className="bg-[#141414] border border-[#262626] rounded text-white text-[11px] px-1.5 py-1"
                      >
                        <option value="assigned_technician">Assigned Technician</option>
                        <option value="helper">Helper / Apprentice</option>
                        <option value="office_support">Office / Support</option>
                        <option value="other">Other</option>
                      </select>
                      <button
                        type="button"
                        data-testid={`shift-crew-remove-${c.employee_id}`}
                        onClick={() => setForm({ ...form, crew: (form.crew || []).filter((x) => x.employee_id !== c.employee_id) })}
                        className="text-[#A3A3A3] hover:text-red-400 text-sm"
                        aria-label={`Remove ${c.name}`}
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider flex items-center gap-1">
                Customer {currentRequiresCustomer && <span className="text-red-400">*</span>}
                {form.move_up_requested && (
                  <span
                    data-testid="shift-customer-moveup-pill"
                    title={form.move_up_notes ? `Move-Up: ${form.move_up_notes}` : 'Wants earlier opening'}
                    className={`ml-2 text-[9px] tracking-wider uppercase px-1.5 py-0.5 rounded border font-bold ${
                      form.move_up_priority === 'high'
                        ? 'text-red-300 border-red-500/50 bg-red-500/10'
                        : 'text-purple-300 border-purple-500/50 bg-purple-500/10'
                    }`}
                  >
                    ↑ Wants Earlier
                  </span>
                )}
              </Label>
              {form.service_type && !currentRequiresCustomer ? (
                <div data-testid="shift-customer-internal-note" className="rounded-md border border-[#262626] bg-[#0A0A0A]/60 px-3 py-2.5 text-[#525252] text-sm italic">
                  Not required for internal shift
                </div>
              ) : (
              <div className="space-y-1.5">
                <div className="flex gap-2 items-center">
                  <Input
                    data-testid="shift-customer-search"
                    value={customerSearch}
                    onChange={(e) => { setCustomerSearch(e.target.value); setCustomerListOpen(true); }}
                    onFocus={() => setCustomerListOpen(true)}
                    placeholder={form.customer_id ? (customers.find((c) => c.id === form.customer_id)?.name || 'Search customers…') : 'Search by name, phone, email, address…'}
                    className="bg-[#0A0A0A] border-[#262626] text-white flex-1"
                  />
                  <Button
                    type="button"
                    data-testid="shift-quick-add-customer"
                    variant="outline"
                    onClick={() => setQuickAddOpen(true)}
                    className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 flex-shrink-0"
                    title="Quick add a new customer"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                  {/* iter74 — Edit selected customer right from dispatch */}
                  {form.customer_id && (
                    <Button
                      type="button"
                      data-testid="shift-edit-customer"
                      variant="outline"
                      onClick={() => {
                        const c = customers.find((x) => x.id === form.customer_id);
                        if (c) setEditCustomerTarget(c);
                      }}
                      className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 flex-shrink-0"
                      title="Edit this customer's info"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                {customerListOpen && (customerSearch.trim() || form.customer_id) && (
                  <div className="bg-[#0A0A0A] border border-[#262626] rounded-md max-h-64 overflow-y-auto" data-testid="shift-customer-results">
                    {form.customer_id && (
                      <button type="button" onClick={() => { handlePickCustomer(''); setCustomerSearch(''); }} className="w-full text-left text-[11px] uppercase tracking-wider px-3 py-2 text-red-400 hover:bg-[#262626] border-b border-[#262626]">
                        Clear selection
                      </button>
                    )}
                    {acctLoading && <div className="px-3 py-3 text-[#A3A3A3] text-xs italic">Searching…</div>}
                    {!acctLoading && customerSearch.trim() && acctResults.length === 0 && (
                      <div className="px-3 py-3 text-[#A3A3A3] text-xs italic">No matches. Use the + button to quick-add a customer.</div>
                    )}
                    {acctResults.slice(0, 30).map((r) => {
                      const badge = r.kind === 'pm_company'
                        ? { t: 'PM COMPANY', c: 'text-[#D4AF37] border-[#D4AF37]/40 bg-[#D4AF37]/10' }
                        : r.kind === 'pm_property'
                        ? { t: 'PROPERTY', c: 'text-cyan-300 border-cyan-700/40 bg-cyan-900/20' }
                        : { t: (r.customer_type || 'customer').replace(/_/g, ' ').toUpperCase(), c: 'text-[#A3A3A3] border-[#404040] bg-[#1a1a1a]' };
                      return (
                        <button
                          type="button"
                          key={`${r.kind}-${r.id}`}
                          data-testid={`shift-acct-opt-${r.kind}-${r.id}`}
                          onClick={() => handleAccountResult(r)}
                          className="w-full text-left px-3 py-2 hover:bg-[#262626] border-b border-[#262626] last:border-b-0"
                        >
                          <p className="text-white text-sm flex items-center gap-2 flex-wrap">
                            {r.name}
                            <span className={`text-[9px] font-bold tracking-wide px-1.5 py-0.5 rounded border ${badge.c}`}>{badge.t}</span>
                            {r.kind === 'pm_company' && <span className="text-[#525252] text-[10px]">▸ pick a property</span>}
                          </p>
                          <p className="text-[#A3A3A3] text-[11px] truncate">
                            {r.kind === 'customer'
                              ? [r.phone, r.email && !String(r.email).endsWith('@noemail.local') ? r.email : null, r.address].filter(Boolean).join(' · ')
                              : r.kind === 'pm_property'
                              ? [r.address, `Property · ${r.company_name}`].filter(Boolean).join(' · ')
                              : r.subtitle}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                )}
                {form.pm_company_id && (
                  <div data-testid="shift-pm-linkage" className="mt-1.5 flex items-center gap-2 flex-wrap rounded border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-2">
                    <span className="text-[9px] font-bold tracking-wide px-1.5 py-0.5 rounded border text-[#D4AF37] border-[#D4AF37]/40">PROPERTY MGMT</span>
                    <span className="text-white text-xs">{form.pm_company_name || 'PM Company'}</span>
                    <span className="text-[#525252]">→</span>
                    {form.property_id
                      ? <span className="text-cyan-300 text-xs">{form.property_name || 'Property'}</span>
                      : <span className="text-amber-300 text-xs italic">Select a property (required to publish)</span>}
                    <button type="button" data-testid="shift-pm-clear" onClick={() => handlePickCustomer('')} className="ml-auto text-[10px] text-red-400 hover:underline">Clear</button>
                  </div>
                )}
                {form.pm_company_id && pmBillingCtx && (
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="shift-pm-billing">
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase tracking-wider text-[#D4AF37]">Bill To (billing account)</Label>
                      <Select value={form.pm_billing_profile_id || ''} onValueChange={(v) => setForm((p) => ({ ...p, pm_billing_profile_id: v }))}>
                        <SelectTrigger data-testid="shift-billto" className="bg-[#0A0A0A] border-[#262626] text-white h-9"><SelectValue placeholder="Select billing account" /></SelectTrigger>
                        <SelectContent className="bg-[#141414] border-[#262626]">
                          {(pmBillingCtx.profiles || []).map((p) => (
                            <SelectItem key={p.id} value={p.id} className="text-white">
                              {p.name}{p.kind !== 'main_ap' ? ' (CC Main AP)' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1 sm:col-span-2">
                      <PmRequesterPicker
                        API={API}
                        companyId={form.pm_company_id}
                        value={{ user_id: form.pm_requester_user_id, pending_id: form.pm_requester_pending_id, name: form.pm_requester_name, state: form.requester_state }}
                        onChange={(v) => setForm((p) => ({ ...p, pm_requester_user_id: v.user_id || '', pm_requester_pending_id: v.pending_id || '', pm_requester_name: v.name || '', requester_state: v.state || '' }))}
                      />
                    </div>
                    <p className="sm:col-span-2 text-[10px] text-[#525252]">Service location stays the property. Bill To / Requester are separate — a manager subaccount auto-CCs Main AP.</p>
                  </div>
                )}
              </div>
              )}
            </div>

            {/* iter273 — PM property sub-picker (opens when a PM company is chosen) */}
            {pmPropertyPicker && (
              <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={() => setPmPropertyPicker(null)}>
                <div data-testid="pm-property-picker" className="bg-[#141414] border border-[#262626] rounded-lg w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-start justify-between p-5 border-b border-[#262626]">
                    <div>
                      <p className="text-[#D4AF37] text-xs font-bold tracking-[0.2em]">SELECT SERVICE LOCATION</p>
                      <h3 className="text-white font-['Outfit'] text-xl font-bold">{pmPropertyPicker.company?.name}</h3>
                      <p className="text-[#A3A3A3] text-xs mt-1">Property Management Company · pick the property (service location) for this job.</p>
                    </div>
                    <button onClick={() => setPmPropertyPicker(null)} className="text-[#525252] hover:text-white"><X className="w-5 h-5" /></button>
                  </div>
                  <div className="p-5 space-y-2">
                    {(pmPropertyPicker.properties || []).length === 0 && !addProp && (
                      <div className="rounded border border-amber-500/40 bg-amber-500/10 p-4 text-amber-200 text-sm">
                        This company has no properties yet — add the service location below.
                      </div>
                    )}
                    {!addProp && (pmPropertyPicker.properties || []).map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        data-testid={`pm-property-opt-${p.id}`}
                        onClick={() => selectPmProperty(p.id)}
                        className="w-full text-left border border-[#262626] rounded p-3 hover:border-[#D4AF37]/40 hover:bg-[#D4AF37]/5"
                      >
                        <p className="text-white text-sm">{p.name}</p>
                        <p className="text-[#A3A3A3] text-[11px]">{p.address || 'No address on file'}</p>
                      </button>
                    ))}

                    {/* Inline Add Property / Service Location */}
                    {!addProp ? (
                      <button type="button" data-testid="pm-property-add-btn"
                        onClick={() => setAddProp({ name: '', address: '', confirmed: false, dups: [] })}
                        className="w-full border border-dashed border-[#262626] rounded p-3 text-[#A3A3A3] hover:text-white hover:border-[#D4AF37]/40 inline-flex items-center justify-center gap-1.5 text-sm">
                        <Plus className="w-4 h-4" /> Add Property / Service Location
                      </button>
                    ) : (
                      <div data-testid="pm-property-add-form" className="border border-[#D4AF37]/30 rounded p-3 space-y-2">
                        <p className="text-[#D4AF37] text-[10px] font-bold uppercase tracking-wider">New Service Location</p>
                        <input data-testid="pm-property-add-name" value={addProp.name}
                          onChange={(e) => setAddProp((f) => ({ ...f, name: e.target.value, confirmed: false, dups: [] }))}
                          placeholder="Property name / label (e.g. 40 Mill St)"
                          className="w-full bg-[#0A0A0A] border border-[#262626] rounded px-2 h-9 text-white text-sm outline-none" />
                        <input data-testid="pm-property-add-address" value={addProp.address}
                          onChange={(e) => setAddProp((f) => ({ ...f, address: e.target.value, confirmed: false, dups: [] }))}
                          placeholder="Service address"
                          className="w-full bg-[#0A0A0A] border border-[#262626] rounded px-2 h-9 text-white text-sm outline-none" />
                        {(addProp.dups || []).length > 0 && (
                          <div className="border border-amber-700/40 bg-amber-500/5 rounded p-2 space-y-1.5" data-testid="pm-property-dup-warning">
                            <p className="text-amber-300 text-[11px]">Possible existing property — use one, or create anyway.</p>
                            {addProp.dups.map((d) => (
                              <button key={d.id} type="button" data-testid={`pm-property-dup-${d.id}`} onClick={() => selectPmProperty(d.id)}
                                className="w-full text-left bg-[#141414] hover:bg-[#1c1c1c] border border-[#262626] rounded px-2 py-1.5">
                                <span className="text-white text-sm">{d.name}</span>
                                <span className="text-[#737373] text-[11px] block">{d.address || 'No address'}</span>
                              </button>
                            ))}
                          </div>
                        )}
                        <div className="flex gap-2">
                          <button type="button" onClick={() => setAddProp(null)} className="px-3 h-8 rounded border border-[#262626] text-[#A3A3A3] hover:bg-[#262626] text-xs">Cancel</button>
                          <button type="button" data-testid="pm-property-add-save" disabled={addPropSaving}
                            onClick={() => selectPmProperty('__inline_create__')}
                            className="flex-1 h-8 rounded bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold text-xs disabled:opacity-50">
                            {addPropSaving ? 'Saving…' : ((addProp.dups || []).length > 0 ? 'Create Anyway' : 'Add & Select')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider flex items-center gap-1"><FileText className="w-3 h-3" /> Work Order #</Label>
                <Input data-testid="shift-wo" value={form.work_order_number} onChange={(e) => setForm({...form, work_order_number: e.target.value})} placeholder="HSP WO, Claim #, NexFlow WO" className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider flex items-center gap-1"><FileText className="w-3 h-3" /> PO Number <span className="text-[#525252] normal-case">(Office-Supplied)</span></Label>
                <Input data-testid="shift-office-po" value={form.office_supplied_po || ''} onChange={(e) => setForm({...form, office_supplied_po: e.target.value})} placeholder="Office PO (separate from WO#)" className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Customer Email</Label>
                <Input data-testid="shift-email" type="email" value={form.customer_email} onChange={(e) => setForm({...form, customer_email: e.target.value})} placeholder="customer@email.com" className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
            </div>

            {/* iter126 — Customer phone on the shift. Auto-filled from customer record when
                a customer is picked. Editable per-shift; saves back to the customer record
                if the customer has no phone yet. */}
            <div className="space-y-1.5">
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider flex items-center gap-1">
                <Phone className="w-3 h-3" /> Customer Phone
                {form.customer_id && form.customer_phone && (
                  <a
                    data-testid="shift-phone-call-link"
                    href={`tel:${form.customer_phone}`}
                    className="ml-2 text-[10px] text-[#D4AF37] hover:underline"
                    title="Call this number"
                  >
                    Call
                  </a>
                )}
              </Label>
              <Input
                data-testid="shift-phone"
                type="tel"
                value={form.customer_phone}
                onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                placeholder="555-555-5555"
                className="bg-[#0A0A0A] border-[#262626] text-white"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Address
                {form.address && (
                  <button
                    type="button"
                    data-testid="shift-address-map-link"
                    onClick={(e) => { e.stopPropagation(); openMapsAddress(form.address); }}
                    className="ml-2 text-[10px] text-[#D4AF37] hover:underline"
                    title="Get directions"
                  >
                    Directions
                  </button>
                )}
              </Label>
              <Input data-testid="shift-address" value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} placeholder="Service location" className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider flex items-center gap-1"><StickyNote className="w-3 h-3" /> Dispatcher Notes (internal)</Label>
              <Textarea data-testid="shift-dispatcher-notes" rows={3} value={form.dispatcher_notes} onChange={(e) => setForm({...form, dispatcher_notes: e.target.value})} placeholder="Dog in backyard, gate code, call before arrival…" className="bg-[#0A0A0A] border-[#262626] text-white" />
              <p className="text-[10px] text-[#A3A3A3]">Visible to dispatch + techs. Hidden from customer.</p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Customer-facing Notes</Label>
              <Textarea data-testid="shift-notes" rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="Notes shared on the appointment record." className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>

            {/* iter124 Wave M — "Wants Earlier Opening" section. Stays attached to the
                real shift — the Move-Up rail uses these fields to surface the customer
                to dispatch without creating duplicate jobs across multiple days.
                iter190 — Now available in create mode too (no more publish → reopen →
                toggle two-step). The Preferred Window text field is replaced by a
                real Earlier Opening Date calendar picker. Legacy free-text window
                is kept as an optional secondary note. */}
            <div data-testid="shift-moveup-section" className="space-y-2 rounded-md border border-purple-500/30 bg-purple-500/[0.04] p-3">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-xs text-purple-300 uppercase tracking-wider flex items-center gap-1.5">
                  ↑ Move-Up / Earlier Appointment
                </Label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-[11px] text-[#A3A3A3]">Wants earlier opening</span>
                  <Switch
                    data-testid="shift-moveup-toggle"
                    checked={!!form.move_up_requested}
                    onCheckedChange={(checked) => setForm({ ...form, move_up_requested: checked })}
                  />
                </label>
              </div>
              {form.move_up_requested && (
                <div className="space-y-2 pt-1" data-testid="shift-moveup-fields">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">Earlier Opening Date</Label>
                      <Input
                        data-testid="shift-moveup-date"
                        type="date"
                        value={form.move_up_date || ''}
                        max={form.date || undefined}
                        onChange={(e) => setForm({ ...form, move_up_date: e.target.value })}
                        className="bg-[#0A0A0A] border-[#262626] text-white h-9"
                      />
                      <p className="text-[10px] text-[#525252]">
                        Real calendar date the customer would take.
                      </p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">Priority</Label>
                      <Select
                        value={form.move_up_priority || 'normal'}
                        onValueChange={(v) => setForm({ ...form, move_up_priority: v })}
                      >
                        <SelectTrigger data-testid="shift-moveup-priority" className="bg-[#0A0A0A] border-[#262626] text-white h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#141414] border-[#262626]">
                          <SelectItem value="low" className="text-white">Low</SelectItem>
                          <SelectItem value="normal" className="text-white">Normal</SelectItem>
                          <SelectItem value="high" className="text-white">High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">Notes for Dispatch</Label>
                    <Textarea
                      data-testid="shift-moveup-notes"
                      rows={2}
                      value={form.move_up_notes || ''}
                      onChange={(e) => setForm({ ...form, move_up_notes: e.target.value })}
                      placeholder="Customer prefers any opening before her current date. Call before arriving."
                      className="bg-[#0A0A0A] border-[#262626] text-white"
                    />
                  </div>
                  <details className="text-[10px] text-[#737373]">
                    <summary className="cursor-pointer hover:text-[#A3A3A3]">Optional: preferred window (free text)</summary>
                    <Input
                      data-testid="shift-moveup-window"
                      value={form.move_up_preferred_window || ''}
                      onChange={(e) => setForm({ ...form, move_up_preferred_window: e.target.value })}
                      placeholder="Mon-Wed AM"
                      className="bg-[#0A0A0A] border-[#262626] text-white h-9 mt-1"
                    />
                  </details>
                  <p className="text-[10px] text-[#525252]">
                    Original scheduled date {form.date ? <><span className="text-[#A3A3A3]">({form.date})</span> </> : ''}stays protected — the customer only moves if dispatch (or a tech) claims the earlier slot via the Move-Up rail. One job, two possible dates.
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider flex items-center gap-1"><Camera className="w-3 h-3" /> Photos for Technician</Label>
              <input
                data-testid="shift-photos-input"
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handlePhotoUpload(e.target.files)}
                className="text-white text-xs file:bg-[#262626] file:text-white file:border-0 file:rounded file:px-2 file:py-1 file:mr-2"
              />
              {form.photo_urls && form.photo_urls.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {form.photo_urls.map((url, idx) => (
                    <div key={idx} className="relative" data-testid={`shift-photo-${idx}`}>
                      <Thumbnail
                        src={url}
                        images={form.photo_urls}
                        index={idx}
                        testId={`shift-photo-img-${idx}`}
                        alt={`Shift photo ${idx + 1}`}
                        className="w-full h-16 object-cover rounded border border-[#262626]"
                      />
                      <button
                        onClick={(e) => { e.stopPropagation(); removePhoto(idx); }}
                        className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px]"
                        data-testid={`shift-photo-remove-${idx}`}
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {editingShift && editingShift.status !== 'draft' && editingShift.customer_id && (
            <div data-testid="manual-notify-actions" className="mt-4 bg-[#0f0f0f] border border-[#262626] rounded-md p-3 space-y-2">
              <p className="text-[#A3A3A3] text-xs">Manual customer comms (use if the auto-flow was skipped)</p>
              <div className="flex gap-2 flex-wrap">
                <Button
                  data-testid="shift-send-confirmation"
                  type="button"
                  onClick={() => handleManualNotify('confirmation')}
                  variant="outline"
                  size="sm"
                  className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                >
                  <Bell className="w-3.5 h-3.5 mr-1.5" /> Send Confirmation
                </Button>
                <Button
                  data-testid="shift-send-update"
                  type="button"
                  onClick={() => handleManualNotify('reschedule')}
                  variant="outline"
                  size="sm"
                  className="border-amber-700 text-amber-300 hover:bg-amber-500/10"
                >
                  <Send className="w-3.5 h-3.5 mr-1.5" /> Send Schedule Update
                </Button>
              </div>
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-[#262626] flex flex-wrap items-center justify-between gap-2 sticky bottom-0 bg-[#141414] pb-2">
            {unscheduledMode ? (
              <>
                {/* iter188 — Primary in unscheduled mode saves to the Holding Area. */}
                <Button
                  data-testid="shift-save-unscheduled"
                  onClick={() => handleSave(true)}
                  className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] flex-1"
                >
                  <Save className="w-4 h-4 mr-1" />
                  Save Unscheduled Job
                </Button>
                {/* iter316 — Schedule Now enables once every required field is present (no tech = Unassigned). */}
                <Button
                  data-testid="shift-schedule-now"
                  onClick={() => handleSave(false)}
                  disabled={unschedMissing.length > 0}
                  variant="outline"
                  className="border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37]/10 disabled:opacity-40"
                  title={unschedMissing.length ? `Missing: ${unschedMissing.join(', ')}` : 'Publish onto the schedule grid now (no tech = Unassigned)'}
                >
                  Schedule Now
                </Button>
              </>
            ) : (
              <>
                <Button data-testid="shift-publish" onClick={() => handleSave(false)} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] flex-1">{editingShift ? 'Save' : 'Publish'}</Button>
                {!editingShift && (
                  <Button data-testid="shift-save-draft" onClick={() => handleSave(true)} variant="outline" className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10" title="Save to the persistent Unscheduled rail (no week/tech yet)"><Save className="w-4 h-4 mr-1" />Draft</Button>
                )}
              </>
            )}
            {editingShift && (
              <Button
                data-testid="shift-duplicate"
                onClick={() => setDuplicateForm({
                  open: true,
                  new_date: editingShift.date,
                  new_start_time: editingShift.start_time,
                  new_end_time: editingShift.end_time,
                  new_employee_id: editingShift.employee_id || '',
                  claim_locked: !!editingShift.claim_locked,
                  note: '',
                })}
                variant="outline"
                className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                title="Repeat work — copies this shift as a NEW job. For another trip on the same job use Add Visit."
              ><Plus className="w-4 h-4 mr-1" />Duplicate</Button>
            )}
            {editingShift && (editingShift.job_id || editingShift.customer_id) && (
              <Button
                data-testid="shift-add-visit"
                onClick={() => { setModalOpen(false); setAddVisit({ open: true, date: editingShift.date, preset: editingShift.job_id ? { jobId: editingShift.job_id } : { appointmentId: editingShift.id, customer_name: editingShift.customer_name, service_type: editingShift.service_type, date: editingShift.date } }); }}
                variant="outline"
                className="border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                title="Schedule another trip for this same job (multi-day / return visit)"
              ><Plus className="w-4 h-4 mr-1" />Add Visit</Button>
            )}
            {editingShift && editingShift.series_id && (
              <div data-testid="series-cancel-block" className="w-full mt-2 p-2 rounded border border-[#262626] bg-[#0A0A0A]">
                <p className="text-[11px] text-[#A3A3A3] uppercase tracking-wider mb-1.5">Recurring series — cancel</p>
                <div className="flex flex-wrap gap-1.5">
                  <Button data-testid="series-cancel-this" onClick={() => cancelSeries('this_only')} variant="outline" size="sm" className="border-[#262626] text-[#A3A3A3] hover:text-white">This shift only</Button>
                  <Button data-testid="series-cancel-future" onClick={() => cancelSeries('this_and_future')} variant="outline" size="sm" className="border-[#262626] text-[#A3A3A3] hover:text-white">This &amp; future</Button>
                  <Button data-testid="series-cancel-all" onClick={() => cancelSeries('entire_series')} variant="outline" size="sm" className="border-red-700/40 text-red-300 hover:bg-red-900/20">Cancel all future</Button>
                </div>
              </div>
            )}
            {editingShift && (
              <Button
                data-testid="shift-cancel-shift"
                onClick={() => setCancelForm({
                  open: true,
                  mode: 'cancel_free',
                  canceled_by: 'customer',
                  reason: '',
                  free_capacity_override: null,   // null → mode default
                  notify_customer: false,
                })}
                variant="outline"
                className="border-orange-700/50 text-orange-300 hover:bg-orange-700/10"
                title="Cancel this shift (3 modes: free slot / keep visible / move to unscheduled)"
              ><X className="w-4 h-4 mr-1" />Cancel Shift</Button>
            )}
            {editingShift && (
              <Button data-testid="shift-delete" onClick={handleDelete} variant="outline" className="border-[#7F1D1D] text-red-400 hover:bg-[#7F1D1D] hover:text-white"><Trash2 className="w-4 h-4" /></Button>
            )}
            <Button onClick={() => setModalOpen(false)} variant="outline" className="border-[#262626] text-white hover:bg-[#262626]"><X className="w-4 h-4" /></Button>
          </div>
        </SheetContent>
      </Sheet>
      <NotifyCustomerDialog
        open={notifyDialog.open}
        mode={notifyDialog.mode}
        customerName={customers.find((c) => c.id === form.customer_id)?.name}
        previewText={notifyDialog.mode === 'reschedule'
          ? `Hi ${(customers.find((c) => c.id === form.customer_id)?.name || '').split(' ')[0] || 'there'}, your NexFlow appointment has been updated to ${form.date} between ${form.start_time} and ${form.end_time}. Please call us with any questions. Reply STOP to opt out.`
          : `Hi ${(customers.find((c) => c.id === form.customer_id)?.name || '').split(' ')[0] || 'there'}, your NexFlow appointment has been scheduled for ${form.date} between ${form.start_time} and ${form.end_time}. Reply STOP to opt out of future messages.`}
        onConfirm={handleNotifyDialogConfirm}
        onCancel={handleNotifyDialogCancel}
      />
      <QuickAddCustomerDialog
        open={quickAddOpen}
        onOpenChange={setQuickAddOpen}
        onCreated={(c) => {
          // iter166 — Pass the new customer object directly so autofill doesn't
          // have to wait for the (async) setCustomers state update to flush.
          setCustomers((prev) => [c, ...prev.filter((p) => p.id !== c.id)]);
          handlePickCustomer(c.id, c);
        }}
        onSelectPmCompany={(companyId, company) => {
          // iter305 — Property Management customer type routes into the canonical PM flow
          // (company → property search/add → requester), never a loose customer.
          setQuickAddOpen(false);
          handleAccountResult({ kind: 'pm_company', id: companyId, name: company?.name });
        }}
      />
      {/* iter97 — Drop-scheduling confirmation. Fired when an unscheduled rail card is dropped
          on any tech/date cell — pre-fills employee + date from the cell. */}
      <ScheduleItemDialog
        item={unschedDropTarget?.item}
        preset={unschedDropTarget?.preset}
        employees={employees}
        onOpenChange={() => setUnschedDropTarget(null)}
        onOpenFull={(it, qs) => { setUnschedDropTarget(null); openEditUnscheduled(withQuickTimes(it, qs)); }}
        onScheduled={() => {
          setUnschedDropTarget(null);
          fetchAppointments();
          if (typeof window.__nexflow_unscheduled_reload === 'function') {
            window.__nexflow_unscheduled_reload();
          }
        }}
      />
      <CustomerEditDialog
        open={!!editCustomerTarget}
        onOpenChange={(o) => !o && setEditCustomerTarget(null)}
        customer={editCustomerTarget}
        onSaved={(updated) => {
          if (!updated) return;
          // Refresh the in-memory customers list so the shift modal picker and saved label stay current.
          setCustomers((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
          // iter249 — if this customer is attached to the open shift, re-hydrate its
          // contact fields from the canonical record so no surface shows stale data.
          if (form.customer_id === updated.id) {
            setForm((p) => ({
              ...p,
              customer_email: (updated.email && !String(updated.email).endsWith('@noemail.local')) ? updated.email : p.customer_email,
              customer_phone: updated.phone || p.customer_phone,
              address: updated.address || p.address,
            }));
          }
        }}
      />

      {/* iter232 — Read-only PTO block details (approved time off is not an editable job). */}
      <Dialog open={!!timeOffView} onOpenChange={(o) => !o && setTimeOffView(null)}>
        <DialogContent data-testid="pto-view-dialog" className="bg-[#141414] border-[#262626] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-300">
              <Lock className="w-4 h-4" /> {timeOffView?.time_off_masked ? 'Scheduled Off — Approved' : 'PTO — Approved Time Off'}
            </DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">
              This is an approved time-off block, not a job. The employee is unavailable on this date.
            </DialogDescription>
          </DialogHeader>
          {timeOffView && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[#A3A3A3]">Employee</span><span className="text-white font-semibold" data-testid="pto-view-employee">{timeOffView.employee_name || '—'}</span></div>
              <div className="flex justify-between"><span className="text-[#A3A3A3]">Date</span><span className="text-white" data-testid="pto-view-date">{timeOffView.date}</span></div>
              {!timeOffView.time_off_masked && (
                <div className="flex justify-between"><span className="text-[#A3A3A3]">Type</span><span className="text-white">{timeOffView.shift_title || 'Time Off'}</span></div>
              )}
              <div className="flex justify-between"><span className="text-[#A3A3A3]">Availability</span><span className="text-red-300 font-semibold" data-testid="pto-view-window">{timeOffWindowLabel(timeOffView)}</span></div>
              {!timeOffView.time_off_masked && typeof timeOffView.paid_leave_hours === 'number' && (
                <div className="flex justify-between"><span className="text-[#A3A3A3]">Paid hours</span><span className="text-white">{timeOffView.paid_leave_hours}h</span></div>
              )}
              {!timeOffView.time_off_masked && timeOffView.notes && (
                <div className="pt-1"><span className="text-[#A3A3A3]">Reason: </span><span className="text-white">{timeOffView.notes}</span></div>
              )}
              {timeOffView.time_off_masked && (
                <p className="text-[#525252] text-xs pt-1">Leave details are private. Only the employee and approvers can see the reason, category and paid hours.</p>
              )}
              {timeOffView.time_off_conflict && (
                <div className="mt-2 rounded-md border border-amber-700/50 bg-amber-950/20 p-2 text-amber-200 text-xs flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  A job was assigned on this day via an authorized override. Both the block and the job are flagged.
                </div>
              )}
              <p className="text-[#525252] text-xs pt-2">To change this, manage the request on the Time Off page.</p>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="pto-view-close" variant="outline" className="border-[#262626] text-[#A3A3A3] hover:text-white" onClick={() => setTimeOffView(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter232 — Assignment override: an authorized user intentionally schedules over approved PTO. */}
      <Dialog open={!!ptoOverride} onOpenChange={(o) => !o && setPtoOverride(null)}>
        <DialogContent data-testid="pto-override-dialog" className="bg-[#141414] border-[#262626] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-300">
              <AlertTriangle className="w-4 h-4" /> Approved Time Off Conflict
            </DialogTitle>
            <DialogDescription className="text-[#A3A3A3]" data-testid="pto-override-message">
              {ptoOverride?.message || 'This employee has approved time off on this date.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-white text-sm">Override reason (required, min 5 characters)</Label>
            <Textarea
              data-testid="pto-override-reason"
              rows={3}
              value={ptoOverride?.reason || ''}
              onChange={(e) => setPtoOverride((p) => p ? { ...p, reason: e.target.value } : p)}
              placeholder="Why is this employee being scheduled over approved time off?"
              className="bg-[#0A0A0A] border-[#262626] text-white"
            />
          </div>
          <DialogFooter>
            <Button data-testid="pto-override-cancel" variant="outline" className="border-[#262626] text-[#A3A3A3] hover:text-white" onClick={() => setPtoOverride(null)}>Cancel</Button>
            <Button
              data-testid="pto-override-confirm"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => {
                const reason = (ptoOverride?.reason || '').trim();
                if (reason.length < 5) { toast.error('An override reason of at least 5 characters is required.'); return; }
                const cb = ptoOverride.onConfirm;
                setPtoOverride(null);
                cb && cb(reason);
              }}
            >
              Override & Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Search Sheet (iter 54) */}
      <Sheet open={searchOpen} onOpenChange={setSearchOpen}>
        <SheetContent side="right" className="bg-[#141414] border-[#262626] w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2"><Search className="w-5 h-5" />Search Schedule</SheetTitle>
            <SheetDescription className="text-[#A3A3A3]">
              Find any shift by customer, address, work order number, or service type.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-3">
            <div className="flex gap-2">
              <Input
                data-testid="schedule-search-q"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                placeholder="Name, phone, email, address, WO#, HSP#, tech, service, notes…"
                className="bg-[#0A0A0A] border-[#262626] text-white"
                autoFocus
              />
              <Select value={searchStatus} onValueChange={(v) => { setSearchStatus(v); setTimeout(() => runSearch(), 0); }}>
                <SelectTrigger data-testid="schedule-search-status" className="bg-[#0A0A0A] border-[#262626] text-white w-44"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  <SelectItem value="all" className="text-white">All statuses</SelectItem>
                  <SelectItem value="open" className="text-white">Open (draft+scheduled+in-progress+pending)</SelectItem>
                  <SelectItem value="draft" className="text-white">Drafts only</SelectItem>
                  <SelectItem value="scheduled" className="text-white">Scheduled</SelectItem>
                  <SelectItem value="in_progress" className="text-white">In progress</SelectItem>
                  <SelectItem value="pending_approval" className="text-white">Pending approval</SelectItem>
                  <SelectItem value="completed" className="text-white">Completed</SelectItem>
                  <SelectItem value="cancelled" className="text-white">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Button data-testid="schedule-search-go" onClick={() => runSearch()} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Search className="w-4 h-4" /></Button>
            </div>
            <p className="text-[10px] text-[#A3A3A3]">Searches across customer name, phone, email, service address, work order #, technician, service type, notes, and dispatcher notes.</p>

            <div className="bg-[#0A0A0A] border border-[#262626] rounded-md divide-y divide-[#262626]" data-testid="schedule-search-results">
              {searchLoading && <div className="p-4 text-center text-[#A3A3A3] text-sm">Searching…</div>}
              {!searchLoading && searchResults.length === 0 && (
                <div className="p-6 text-center text-[#A3A3A3] text-sm">{searchQ.trim() ? 'No results.' : 'Type a search term and press Enter.'}</div>
              )}
              {searchResults.map((r) => (
                <button
                  key={r.id}
                  data-testid={`schedule-search-row-${r.id}`}
                  onClick={() => openShiftFromSearch(r)}
                  className="w-full text-left p-3 hover:bg-[#141414] transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{r.customer_name || r.shift_title || '(no customer)'}</p>
                      <p className="text-[#A3A3A3] text-xs truncate">
                        {r.date} {r.start_time ? `${r.start_time}–${r.end_time}` : ''} · {r.service_type}
                        {r.employee_name && <span> · {r.employee_name}</span>}
                      </p>
                      {(r.address || r.work_order_number) && (
                        <p className="text-[#525252] text-[11px] truncate">
                          {r.address ? `${r.address}` : ''}
                          {r.work_order_number ? ` · WO ${r.work_order_number}` : ''}
                        </p>
                      )}
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded uppercase whitespace-nowrap ${{
                      draft: 'bg-[#525252]/30 text-[#A3A3A3]',
                      scheduled: 'bg-[#D4AF37]/20 text-[#D4AF37]',
                      in_progress: 'bg-blue-500/20 text-blue-300',
                      pending_approval: 'bg-orange-500/20 text-orange-300',
                      completed: 'bg-green-500/20 text-green-300',
                      cancelled: 'bg-red-500/20 text-red-300',
                    }[r.status] || 'bg-[#262626] text-[#A3A3A3]'}`}>{r.status?.replace('_', ' ')}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* iter131 — Duplicate Shift dialog. Copies the full job packet to a new date/time/tech. */}
      <Sheet open={!!duplicateForm.open} onOpenChange={(o) => { if (!o) setDuplicateForm({ open: false }); }}>
        <SheetContent side="right" className="bg-[#141414] border-l border-[#262626] text-white w-full sm:max-w-md" data-testid="duplicate-shift-sheet">
          {duplicateForm.open && editingShift && (() => {
            const isCompletedSrc = (editingShift.status || '').toLowerCase() === 'completed' || !!editingShift.completion_id;
            return (
            <>
              <SheetHeader>
                <SheetTitle className="text-white">{isCompletedSrc ? 'Duplicate Completed Shift to Unscheduled' : 'Duplicate Shift to New Day'}</SheetTitle>
                <SheetDescription className="text-[#A3A3A3]">
                  {isCompletedSrc
                    ? 'This shift is already completed. The duplicate will land on the Unscheduled rail as claimable so dispatch (or a tech) can re-post it. Completion, signatures, payment, billing, and review state are NOT copied.'
                    : 'Copies the customer, address, phone, WO#, internal/customer notes, helpers, and photos. Completion status, signatures, and payment are NOT copied.'}
                </SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-3">
                {isCompletedSrc ? (
                  <div data-testid="dup-completed-banner" className="rounded-md border border-blue-500/40 bg-blue-500/10 p-3 text-xs text-blue-200">
                    <p className="font-bold uppercase tracking-wider mb-1">Goes to Unscheduled Rail</p>
                    <p>No date/time picker — dispatch (or a tech) will schedule or claim it from the rail.</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">New Date</Label>
                        <Input data-testid="dup-date" type="date" value={duplicateForm.new_date || ''} onChange={(e) => setDuplicateForm({ ...duplicateForm, new_date: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white h-9" />
                      </div>
                      <div>
                        <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Assigned Tech</Label>
                        <Select value={duplicateForm.new_employee_id || 'unassigned'} onValueChange={(v) => setDuplicateForm({ ...duplicateForm, new_employee_id: v === 'unassigned' ? '' : v })}>
                          <SelectTrigger data-testid="dup-tech" className="bg-[#0A0A0A] border-[#262626] text-white h-9"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[#141414] border-[#262626]">
                            <SelectItem value="unassigned" className="text-white">Unassigned</SelectItem>
                            {employees.map((e) => (<SelectItem key={e.id} value={e.id} className="text-white">{e.name}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Start</Label>
                        <Input data-testid="dup-start" type="time" value={duplicateForm.new_start_time || ''} onChange={(e) => setDuplicateForm({ ...duplicateForm, new_start_time: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white h-9" />
                      </div>
                      <div>
                        <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">End</Label>
                        <Input data-testid="dup-end" type="time" value={duplicateForm.new_end_time || ''} onChange={(e) => setDuplicateForm({ ...duplicateForm, new_end_time: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white h-9" />
                      </div>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input data-testid="dup-claim-lock" type="checkbox" checked={!!duplicateForm.claim_locked} onChange={(e) => setDuplicateForm({ ...duplicateForm, claim_locked: e.target.checked })} className="accent-[#D4AF37]" />
                      <span className="text-sm text-white">Lock from tech claiming (rare — hides from claim queue)</span>
                    </label>
                  </>
                )}
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Note (optional)</Label>
                  <Textarea
                    data-testid="dup-note"
                    rows={2}
                    value={duplicateForm.note || ''}
                    onChange={(e) => setDuplicateForm({ ...duplicateForm, note: e.target.value })}
                    placeholder={isCompletedSrc
                      ? `Defaults to "Duplicated from completed shift on ${editingShift.date}"`
                      : `Defaults to "Duplicated from shift on ${editingShift.date}"`}
                    className="bg-[#0A0A0A] border-[#262626] text-white"
                  />
                </div>
                <div className="rounded-md border border-[#262626] bg-[#0A0A0A] p-2 text-[10px] text-[#A3A3A3]">
                  <p className="font-bold uppercase tracking-wider mb-1">Will copy</p>
                  <p>Customer · phone · email · address · service type · WO# · helpers · internal & customer-facing notes · reference photos · template</p>
                  <p className="font-bold uppercase tracking-wider mt-2 mb-1">Will NOT copy</p>
                  <p>Completion status · signatures · payment · invoice/billing · review state · closeout answers · move-up flag · arrival ETA · HSP payment history</p>
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <Button data-testid="dup-cancel" variant="outline" onClick={() => setDuplicateForm({ open: false })} className="border-[#262626] text-white flex-1">Cancel</Button>
                  <Button
                    data-testid="dup-confirm"
                    onClick={async () => {
                      if (!isCompletedSrc && (!duplicateForm.new_date || !duplicateForm.new_start_time || !duplicateForm.new_end_time)) {
                        toast.error('Pick a date and time window');
                        return;
                      }
                      try {
                        const body = isCompletedSrc
                          ? { note: duplicateForm.note || '' }
                          : {
                              new_date: duplicateForm.new_date,
                              new_start_time: duplicateForm.new_start_time,
                              new_end_time: duplicateForm.new_end_time,
                              new_employee_id: duplicateForm.new_employee_id || null,
                              claim_locked: !!duplicateForm.claim_locked,
                              note: duplicateForm.note || '',
                            };
                        const { data } = await axios.post(`${API}/admin/appointments/${editingShift.id}/duplicate`, body, { withCredentials: true });
                        if (data.to_unscheduled) {
                          toast.success('Duplicated to Unscheduled rail');
                        } else {
                          toast.success(`Duplicated to ${data.appointment.date}`);
                        }
                        setDuplicateForm({ open: false });
                        setModalOpen(false);
                        await fetchAppointments();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Failed to duplicate');
                      }
                    }}
                    className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold flex-1"
                  >
                    {isCompletedSrc ? 'Send to Unscheduled' : 'Duplicate'}
                  </Button>
                </div>
              </div>
            </>
            );
          })()}
        </SheetContent>
      </Sheet>

      {/* iter142 — Cancel Shift sheet. Three modes plus structured cancellation metadata. */}
      <Sheet open={!!cancelForm.open} onOpenChange={(o) => { if (!o) setCancelForm({ open: false }); }}>
        <SheetContent side="right" data-testid="cancel-shift-sheet" className="bg-[#141414] border-[#262626] text-white w-full sm:max-w-md overflow-y-auto">
          {cancelForm.open && editingShift && (() => {
            const modeDescriptions = {
              cancel_free: 'Mark cancelled, clear the tech assignment, and FREE the slot. Default for customer cancellations.',
              cancel_hold: 'Mark cancelled but keep the card visible on the schedule with a Cancelled badge. Slot is NOT freed.',
              cancel_to_unscheduled: 'Mark cancelled and move the job to the Unscheduled rail so it can be rescheduled later.',
            };
            const defaultFreeForMode = cancelForm.mode !== 'cancel_hold';
            const effectiveFreeCapacity = cancelForm.free_capacity_override == null ? defaultFreeForMode : cancelForm.free_capacity_override;
            const hasContact = !!(editingShift.customer_phone || editingShift.customer_email);
            return (
              <>
                <SheetHeader>
                  <SheetTitle className="text-white">Cancel Shift</SheetTitle>
                  <SheetDescription className="text-[#A3A3A3]">
                    {editingShift.shift_title || editingShift.service_type || 'Job'} · {editingShift.customer_name || 'No customer'}<br />
                    {editingShift.date} {editingShift.start_time}–{editingShift.end_time}
                  </SheetDescription>
                </SheetHeader>
                <div className="mt-4 space-y-3">
                  <div>
                    <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Mode *</Label>
                    <div className="grid grid-cols-1 gap-2 mt-1">
                      {[
                        ['cancel_free', 'Cancel & free slot', 'border-orange-700/50 bg-orange-700/10 text-orange-300'],
                        ['cancel_hold', 'Cancel but keep visible (Cancelled badge)', 'border-[#525252] bg-[#262626] text-[#D4D4D4]'],
                        ['cancel_to_unscheduled', 'Move to Unscheduled / Reschedule later', 'border-blue-500/40 bg-blue-500/10 text-blue-200'],
                      ].map(([k, label, cls]) => (
                        <button
                          type="button"
                          key={k}
                          data-testid={`cancel-mode-${k}`}
                          onClick={() => setCancelForm({ ...cancelForm, mode: k, free_capacity_override: null })}
                          className={`text-left rounded-md border p-2 text-sm transition ${cls} ${cancelForm.mode === k ? 'ring-2 ring-[#D4AF37]' : 'opacity-70 hover:opacity-100'}`}
                        >
                          <p className="font-bold">{label}</p>
                          <p className="text-[11px] mt-0.5 opacity-80">{modeDescriptions[k]}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Who canceled? *</Label>
                      <Select value={cancelForm.canceled_by || 'customer'} onValueChange={(v) => setCancelForm({ ...cancelForm, canceled_by: v })}>
                        <SelectTrigger data-testid="cancel-canceled-by" className="bg-[#0A0A0A] border-[#262626] text-white h-9"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#141414] border-[#262626]">
                          <SelectItem value="customer" className="text-white">Customer canceled</SelectItem>
                          <SelectItem value="nexflow" className="text-white">Nexflow canceled</SelectItem>
                          <SelectItem value="hsp_issue" className="text-white">HSP / warranty issue</SelectItem>
                          <SelectItem value="weather_emergency" className="text-white">Weather / emergency</SelectItem>
                          <SelectItem value="other" className="text-white">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {cancelForm.mode !== 'cancel_to_unscheduled' && (
                      <div>
                        <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Free Capacity?</Label>
                        <Select
                          value={effectiveFreeCapacity ? 'yes' : 'no'}
                          onValueChange={(v) => setCancelForm({ ...cancelForm, free_capacity_override: v === 'yes' })}
                        >
                          <SelectTrigger data-testid="cancel-free-capacity" className="bg-[#0A0A0A] border-[#262626] text-white h-9"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[#141414] border-[#262626]">
                            <SelectItem value="yes" className="text-white">Yes — release slot {cancelForm.mode === 'cancel_free' && !cancelForm.free_capacity_override && '(default)'}</SelectItem>
                            <SelectItem value="no" className="text-white">No — keep slot held {cancelForm.mode === 'cancel_hold' && cancelForm.free_capacity_override == null && '(default)'}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  <div>
                    <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Reason / Note</Label>
                    <Textarea
                      data-testid="cancel-reason"
                      value={cancelForm.reason || ''}
                      onChange={(e) => setCancelForm({ ...cancelForm, reason: e.target.value })}
                      rows={2}
                      placeholder="Required if canceling for a non-customer reason (weather, tech issue, etc.)"
                      className="bg-[#0A0A0A] border-[#262626] text-white"
                    />
                  </div>

                  <label className="flex items-start gap-2 cursor-pointer rounded-md border border-[#262626] bg-[#0A0A0A] p-2">
                    <input
                      data-testid="cancel-notify-customer"
                      type="checkbox"
                      checked={!!cancelForm.notify_customer && hasContact}
                      disabled={!hasContact}
                      onChange={(e) => setCancelForm({ ...cancelForm, notify_customer: e.target.checked })}
                      className="accent-[#D4AF37] mt-0.5"
                    />
                    <span className="text-xs">
                      <span className={hasContact ? 'text-white font-bold' : 'text-[#525252] font-bold'}>
                        Send cancellation notice to customer{!hasContact && ' (no phone/email on file)'}
                      </span>
                      <br />
                      <span className="text-[#A3A3A3]">
                        SMS preferred if a phone is on file, otherwise email. We never auto-send — only when you check this box.
                      </span>
                    </span>
                  </label>

                  <div className="flex items-center gap-2 pt-2">
                    <Button data-testid="cancel-shift-cancel" variant="outline" onClick={() => setCancelForm({ open: false })} className="border-[#262626] text-white flex-1">Back</Button>
                    <Button
                      data-testid="cancel-shift-confirm"
                      onClick={async () => {
                        if (cancelForm.canceled_by !== 'customer' && !cancelForm.reason?.trim()) {
                          toast.error('Reason required for non-customer cancellations');
                          return;
                        }
                        const body = {
                          mode: cancelForm.mode,
                          canceled_by: cancelForm.canceled_by,
                          reason: cancelForm.reason || '',
                          notify_customer: !!cancelForm.notify_customer && hasContact,
                        };
                        if (cancelForm.mode !== 'cancel_to_unscheduled' && cancelForm.free_capacity_override != null) {
                          body.free_capacity = !!cancelForm.free_capacity_override;
                        }
                        try {
                          const { data } = await axios.post(`${API}/admin/appointments/${editingShift.id}/cancel-shift`, body, { withCredentials: true });
                          let msg = 'Shift cancelled';
                          if (data.mode === 'cancel_to_unscheduled') msg = 'Cancelled and moved to Unscheduled';
                          else if (data.mode === 'cancel_hold') msg = 'Cancelled (kept visible on schedule)';
                          else if (data.mode === 'cancel_free') msg = data.free_capacity ? 'Cancelled · slot freed' : 'Cancelled';
                          if (data.notify_result?.sent) msg += ' · customer notified';
                          else if (cancelForm.notify_customer && !data.notify_result?.sent) {
                            const why = data.notify_result?.reason || (data.notify_result?.error || '').slice(0, 60) || 'unknown';
                            msg += ` · notification not sent (${why})`;
                          }
                          toast.success(msg);
                          setCancelForm({ open: false });
                          setModalOpen(false);
                          await fetchAppointments();
                          if (data.mode === 'cancel_to_unscheduled' && typeof window.__nexflow_unscheduled_reload === 'function') {
                            await window.__nexflow_unscheduled_reload();
                          }
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Failed to cancel shift');
                        }
                      }}
                      className="bg-orange-600 hover:bg-orange-700 text-white font-bold flex-1"
                    >
                      Confirm Cancel
                    </Button>
                  </div>
                </div>
              </>
            );
          })()}
        </SheetContent>
      </Sheet>

      <AdminCompletionEditSheet
        open={!!editingCompletionId}
        onOpenChange={(o) => { if (!o) setEditingCompletionId(null); }}
        completionId={editingCompletionId}
        onSaved={() => { fetchAppointments(); }}
      />

      {/* iter124 Wave M — Move-Up List sheet (dispatch surface for "wants earlier opening"). */}
      <MoveUpListSheet
        open={moveUpSheetOpen}
        onOpenChange={setMoveUpSheetOpen}
        employees={employees}
        onMoved={() => { fetchAppointments(); }}
        askNotify={async ({ customer_name, from, to }) => {
          // Reuse the simple confirm flow — keeps parity with how the reschedule notify
          // dialog is invoked elsewhere on Schedule.js without splicing a stateful dialog
          // into the rail. If the user wants the richer NotifyCustomerDialog later we can
          // promote this to setNotifyDialog({ open: true, mode: 'reschedule', ... }).
          if (!customer_name) return false;
          return window.confirm(
            `Notify ${customer_name} via SMS that their appointment moved from ${from} → ${to}?\n\nOK = send reschedule text. Cancel = move silently.`
          );
        }}
      />

      {/* iter125 — Manual per-day status editor. Override takes precedence over auto capacity. */}
      <Sheet open={!!dayStatusEditor} onOpenChange={(o) => { if (!o) setDayStatusEditor(null); }}>
        <SheetContent side="right" className="bg-[#141414] border-l border-[#262626] text-white w-full sm:max-w-md" data-testid="day-status-sheet">
          {dayStatusEditor && (
            <>
              <SheetHeader>
                <SheetTitle className="text-white">Day Status — {dayStatusEditor.date}</SheetTitle>
                <SheetDescription className="text-[#A3A3A3]">
                  Manually mark this day as Open, Limited, or Full. This overrides the automatic capacity calculation until cleared.
                </SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Status</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { v: 'OPEN',    cls: 'border-green-700/50 text-green-300 bg-green-500/10' },
                      { v: 'LIMITED', cls: 'border-amber-700/50 text-amber-300 bg-amber-500/10' },
                      { v: 'FULL',    cls: 'border-red-700/50 text-red-300 bg-red-500/10' },
                      { v: '',        cls: 'border-[#262626] text-[#A3A3A3] bg-[#0A0A0A]' },
                    ].map((opt) => (
                      <button
                        key={opt.v || 'auto'}
                        type="button"
                        data-testid={`day-status-option-${opt.v ? opt.v.toLowerCase() : 'auto'}`}
                        onClick={() => setDayStatusEditor({ ...dayStatusEditor, status: opt.v })}
                        className={`text-[10px] font-bold uppercase tracking-wider px-2 py-2 rounded border ${opt.cls} ${dayStatusEditor.status === opt.v ? 'ring-2 ring-[#D4AF37]' : 'opacity-60 hover:opacity-100'}`}
                      >
                        {opt.v || 'Auto'}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-[#525252]">Pick &quot;Auto&quot; to clear the override and revert to the auto capacity calculation.</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Reason / Note (optional)</Label>
                  <Textarea
                    data-testid="day-status-note"
                    rows={2}
                    value={dayStatusEditor.note}
                    onChange={(e) => setDayStatusEditor({ ...dayStatusEditor, note: e.target.value })}
                    placeholder="Holding for emergency, big job, short staffed…"
                    className="bg-[#0A0A0A] border-[#262626] text-white"
                  />
                </div>
                {capacityByDate[dayStatusEditor.date] && (
                  <div className="rounded-md border border-[#262626] bg-[#0A0A0A] p-3 text-xs text-[#A3A3A3]">
                    <p className="uppercase tracking-wider text-[10px] mb-1">Auto Capacity</p>
                    <p>{capacityByDate[dayStatusEditor.date].status} — {Math.round((capacityByDate[dayStatusEditor.date].utilisation || 0) * 100)}% utilised ({Math.round((capacityByDate[dayStatusEditor.date].booked_minutes || 0) / 60)}h of {Math.round((capacityByDate[dayStatusEditor.date].capacity_minutes || 0) / 60)}h)</p>
                  </div>
                )}
                <div className="flex items-center gap-2 pt-2">
                  <Button
                    data-testid="day-status-cancel"
                    variant="outline"
                    onClick={() => setDayStatusEditor(null)}
                    className="border-[#262626] text-white flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    data-testid="day-status-save"
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/admin/schedule/day-status`, {
                          date: dayStatusEditor.date,
                          status: dayStatusEditor.status || '',
                          note: dayStatusEditor.note || '',
                        }, { withCredentials: true });
                        toast.success(dayStatusEditor.status ? `Day marked ${dayStatusEditor.status}` : 'Day status cleared (auto)');
                        setDayStatusEditor(null);
                        await fetchAppointments();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Failed to save day status');
                      }
                    }}
                    className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold flex-1"
                  >
                    Save
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* iter167 — Billing Missing drill-down sheet. Lists completed
          customer-facing shifts with no documented revenue outcome. Each row
          links to the appointment edit drawer so the dispatcher / office can
          either open Correct Completion, finish billing, or set a no-billing
          reason. */}
      <Sheet open={billingMissingOpen} onOpenChange={setBillingMissingOpen}>
        <SheetContent
          data-testid="billing-missing-sheet"
          className="bg-[#141414] border-l border-[#262626] text-white w-full sm:max-w-xl overflow-y-auto"
        >
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
              Billing Missing
              <span className="text-red-300 font-bold">{billingMissing.count}</span>
            </SheetTitle>
            <p className="text-[#A3A3A3] text-xs leading-relaxed">
              Completed customer-facing jobs this week with no invoice, draft, warranty/HSP record,
              $0 membership invoice, or no-billing reason. Open each to finish billing or correct the completion.
            </p>
          </SheetHeader>
          <div className="mt-4 space-y-2">
            {billingMissing.items.length === 0 && (
              <p className="text-[#A3A3A3] text-sm text-center py-8">
                All completed jobs this week have a documented revenue outcome.
              </p>
            )}
            {billingMissing.items.map((row) => (
              <div
                key={row.id}
                data-testid={`billing-missing-row-${row.id}`}
                className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3 flex flex-col gap-1.5"
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 text-xs text-[#A3A3A3]">
                    <span className="text-white">{row.date}</span>
                    {row.start_time && <span>· {row.start_time}</span>}
                    {row.employee_name && <span>· {row.employee_name}</span>}
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-red-300 bg-red-900/30 border border-red-800/60 rounded px-1.5 py-0.5">
                    {row.status || 'completed'}
                  </span>
                </div>
                <div className="text-sm text-white font-semibold">{row.customer_name || '(no customer)'}</div>
                {row.shift_title && (
                  <div className="text-xs text-[#A3A3A3]">{row.shift_title}</div>
                )}
                <div className="flex gap-2 mt-1">
                  <Button
                    data-testid={`billing-missing-open-${row.id}`}
                    onClick={() => {
                      // Open the existing Edit Shift modal for this appointment.
                      const appt = appointments.find((a) => a.id === row.id);
                      if (appt) {
                        openEditShift(appt);
                        setBillingMissingOpen(false);
                      } else {
                        toast.info('Shift not in current week view — switch weeks to find it.');
                      }
                    }}
                    className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] h-7 text-xs"
                  >
                    Open shift
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Schedule;
